// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext.js';
import { LocaleProvider } from './contexts/LocaleContext.js';
import { PermissionProvider, usePermissionContext } from './contexts/PermissionContext.js';
import { ModuleConfigProvider, useModuleConfig } from './contexts/ModuleConfigContext.js';
import { ToastProvider } from './components/Toast.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { ProtectedRoute } from './components/ProtectedRoute.js';
import { SkeletonLoader } from './components/SkeletonLoader.js';
import { TourRouteCommitMarker } from './components/TourRouteCommitMarker.js';
import { IntakeFormPage } from './pages/public/IntakeFormPage.js';
import { IntakeConfirmationPage } from './pages/public/IntakeConfirmationPage.js';
import { AccessDeniedPage } from './pages/admin/AccessDeniedPage.js';
import { useSectorOverlayNavigation } from './hooks/useSectorOverlayNavigation.js';
import { getRouteModule, routeOpensFor } from './config/route-permissions.js';
import { apiClient } from './services/api-client.js';
// Public auth pages (not lazy-loaded)
import { LoginPage } from './pages/LoginPage.js';
import { SsoHandoffPage } from './pages/SsoHandoffPage.js';
import { MfaEnrolmentRequiredPage } from './pages/MfaEnrolmentRequiredPage.js';
import { PasswordChangeRequiredPage } from './pages/PasswordChangeRequiredPage.js';
import { MfaStepUpPage } from './pages/MfaStepUpPage.js';
import { MfaStepUpRedirector } from './components/MfaStepUpRedirector.js';
import { PasswordChangeRedirector } from './components/PasswordChangeRedirector.js';
import { ConsentPage as OAuthConsentPage } from './pages/oauth/ConsentPage.js';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage.js';
import { ResetPasswordPage } from './pages/ResetPasswordPage.js';
import { VerifyEmailPage } from './pages/VerifyEmailPage.js';
// Layout (not lazy-loaded)
import { AdminLayout } from './layouts/AdminLayout.js';

// --- Lazy-loaded admin pages ---

// Sprint 3.0.0 Login + User/Role Management
const MfaSettingsPage = lazy(() =>
  import('./pages/admin/security/MfaSettingsPage.js').then((m) => ({ default: m.MfaSettingsPage })),
);
const SsoConfigPage = lazy(() =>
  import('./pages/admin/security/SsoConfigPage.js').then((m) => ({ default: m.SsoConfigPage })),
);
// Sprint 3.2.0 Security pages
const ApiKeysPage = lazy(() =>
  import('./pages/admin/security/ApiKeysPage.js').then((m) => ({ default: m.ApiKeysPage })),
);
const ServiceAccountsPage = lazy(() =>
  import('./pages/admin/security/ServiceAccountsPage.js').then((m) => ({
    default: m.ServiceAccountsPage,
  })),
);
const IpAllowlistPage = lazy(() =>
  import('./pages/admin/security/IpAllowlistPage.js').then((m) => ({ default: m.IpAllowlistPage })),
);
const DelegationsPage = lazy(() =>
  import('./pages/admin/security/DelegationsPage.js').then((m) => ({ default: m.DelegationsPage })),
);
const BreakGlassPage = lazy(() =>
  import('./pages/admin/security/BreakGlassPage.js').then((m) => ({ default: m.BreakGlassPage })),
);
const SessionSettingsPage = lazy(() =>
  import('./pages/admin/security/SessionSettingsPage.js').then((m) => ({
    default: m.SessionSettingsPage,
  })),
);
const ActiveSessionsPage = lazy(() =>
  import('./pages/admin/security/ActiveSessionsPage.js').then((m) => ({
    default: m.ActiveSessionsPage,
  })),
);
// Sprint 3.3.0 Access Review, Attestation, SoD pages
const AccessReviewListPage = lazy(() =>
  import('./pages/admin/access-reviews/AccessReviewListPage.js').then((m) => ({
    default: m.AccessReviewListPage,
  })),
);
const CreateCampaignPage = lazy(() =>
  import('./pages/admin/access-reviews/CreateCampaignPage.js').then((m) => ({
    default: m.CreateCampaignPage,
  })),
);
const CampaignDetailPage = lazy(() =>
  import('./pages/admin/access-reviews/CampaignDetailPage.js').then((m) => ({
    default: m.CampaignDetailPage,
  })),
);
const MyReviewsPage = lazy(() =>
  import('./pages/admin/access-reviews/MyReviewsPage.js').then((m) => ({
    default: m.MyReviewsPage,
  })),
);
const RoleAnalyticsPage = lazy(() =>
  import('./pages/admin/access-reviews/RoleAnalyticsPage.js').then((m) => ({
    default: m.RoleAnalyticsPage,
  })),
);
const AttestationSchedulesPage = lazy(() =>
  import('./pages/admin/attestation-schedules/AttestationSchedulesPage.js').then((m) => ({
    default: m.AttestationSchedulesPage,
  })),
);
const CreateSchedulePage = lazy(() =>
  import('./pages/admin/attestation-schedules/CreateSchedulePage.js').then((m) => ({
    default: m.CreateSchedulePage,
  })),
);
const EditSchedulePage = lazy(() =>
  import('./pages/admin/attestation-schedules/EditSchedulePage.js').then((m) => ({
    default: m.EditSchedulePage,
  })),
);
const ScheduleHistoryPage = lazy(() =>
  import('./pages/admin/attestation-schedules/ScheduleHistoryPage.js').then((m) => ({
    default: m.ScheduleHistoryPage,
  })),
);
const SodRulesListPage = lazy(() =>
  import('./pages/admin/sod-rules/SodRulesListPage.js').then((m) => ({
    default: m.SodRulesListPage,
  })),
);
const CreateSodRulePage = lazy(() =>
  import('./pages/admin/sod-rules/CreateSodRulePage.js').then((m) => ({
    default: m.CreateSodRulePage,
  })),
);
const EditSodRulePage = lazy(() =>
  import('./pages/admin/sod-rules/EditSodRulePage.js').then((m) => ({
    default: m.EditSodRulePage,
  })),
);
const SodViolationsPage = lazy(() =>
  import('./pages/admin/sod-rules/SodViolationsPage.js').then((m) => ({
    default: m.SodViolationsPage,
  })),
);
const SodExceptionsPage = lazy(() =>
  import('./pages/admin/sod-rules/SodExceptionsPage.js').then((m) => ({
    default: m.SodExceptionsPage,
  })),
);
const ProfilePage = lazy(() =>
  import('./pages/admin/profile/ProfilePage.js').then((m) => ({ default: m.ProfilePage })),
);
const NotificationPreferencesPage = lazy(
  () => import('./pages/admin/platform/NotificationPreferencesPage.js'),
);
const NotificationAdminQueuePage = lazy(
  () => import('./pages/admin/platform/NotificationAdminQueuePage.js'),
);
const UsersPage = lazy(() =>
  import('./pages/admin/users/UsersPage.js').then((m) => ({ default: m.UsersPage })),
);
const UserDetailPage = lazy(() =>
  import('./pages/admin/users/UserDetailPage.js').then((m) => ({ default: m.UserDetailPage })),
);
const RolesPage = lazy(() =>
  import('./pages/admin/roles/RolesPage.js').then((m) => ({ default: m.RolesPage })),
);
const RoleDetailPage = lazy(() =>
  import('./pages/admin/roles/RoleDetailPage.js').then((m) => ({ default: m.RoleDetailPage })),
);
const TicketListPage = lazy(() =>
  import('./pages/admin/TicketListPage.js').then((m) => ({ default: m.TicketListPage })),
);
const TicketDetailPage = lazy(() =>
  import('./pages/admin/TicketDetailPage.js').then((m) => ({ default: m.TicketDetailPage })),
);
const CreateCasePage = lazy(() =>
  import('./pages/admin/CreateCasePage.js').then((m) => ({ default: m.CreateCasePage })),
);
// Sprint 4.3.0 CMP DSAR sync settings
const CmpSyncSettingsPage = lazy(() =>
  import('./pages/admin/rights-requests/CmpSyncSettingsPage.js').then((m) => ({
    default: m.CmpSyncSettingsPage,
  })),
);
const SettingsPage = lazy(() =>
  import('./pages/admin/SettingsPage.js').then((m) => ({ default: m.SettingsPage })),
);
const AuditTrailPage = lazy(() =>
  import('./pages/admin/AuditTrailPage.js').then((m) => ({ default: m.AuditTrailPage })),
);
// Sprint 22.0.0 Audit Trail Maturity pages
const UnifiedAuditPage = lazy(() =>
  import('./pages/admin/audit/UnifiedAuditPage.js').then((m) => ({
    default: m.UnifiedAuditPage,
  })),
);
// Sprint 62.2.5 — cross-framework evidence packs (builder, detail, list).
const FrameworkEvidencePackListPage = lazy(() =>
  import('./pages/admin/framework-evidence-packs/EvidencePackListPage.js').then((m) => ({
    default: m.EvidencePackListPage,
  })),
);
const FrameworkEvidencePackBuilderPage = lazy(() =>
  import('./pages/admin/framework-evidence-packs/EvidenceReportBuilderPage.js').then((m) => ({
    default: m.EvidenceReportBuilderPage,
  })),
);
const FrameworkEvidencePackDetailPage = lazy(() =>
  import('./pages/admin/framework-evidence-packs/EvidencePackDetailPage.js').then((m) => ({
    default: m.EvidencePackDetailPage,
  })),
);
// Sprint 62.3.0 — Vietnam AI governance operations (register, form, detail).
const AiInventoryReviewsRegisterPage = lazy(() =>
  import('./pages/admin/ai-governance/registers/AiRegisterPages.js').then((m) => ({
    default: m.AiInventoryReviewsRegisterPage,
  })),
);
const AiPaymentAuthoritiesRegisterPage = lazy(() =>
  import('./pages/admin/ai-governance/registers/AiRegisterPages.js').then((m) => ({
    default: m.AiPaymentAuthoritiesRegisterPage,
  })),
);
const AiInterventionsRegisterPage = lazy(() =>
  import('./pages/admin/ai-governance/registers/AiRegisterPages.js').then((m) => ({
    default: m.AiInterventionsRegisterPage,
  })),
);
const AiComplaintsRegisterPage = lazy(() =>
  import('./pages/admin/ai-governance/registers/AiRegisterPages.js').then((m) => ({
    default: m.AiComplaintsRegisterPage,
  })),
);
const AiFrameworkLinesRegisterPage = lazy(() =>
  import('./pages/admin/ai-governance/registers/AiRegisterPages.js').then((m) => ({
    default: m.AiFrameworkLinesRegisterPage,
  })),
);
const AiBoardFiguresRegisterPage = lazy(() =>
  import('./pages/admin/ai-governance/registers/AiRegisterPages.js').then((m) => ({
    default: m.AiBoardFiguresRegisterPage,
  })),
);
const AiSystemRegisterPage = lazy(() =>
  import('./pages/admin/ai-governance/AiSystemRegisterPage.js').then((m) => ({
    default: m.AiSystemRegisterPage,
  })),
);
const AiSystemRegisterFormPage = lazy(() =>
  import('./pages/admin/ai-governance/AiSystemRegisterFormPage.js').then((m) => ({
    default: m.AiSystemRegisterFormPage,
  })),
);
const AiSystemDetailPage = lazy(() =>
  import('./pages/admin/ai-governance/AiSystemDetailPage.js').then((m) => ({
    default: m.AiSystemDetailPage,
  })),
);
// Sprint 62.4.0 — regulatory classification (register, profile form,
// screening, detail, offerings register and form).
const ClassificationSubjectRegisterPage = lazy(() =>
  import('./pages/admin/regulatory-classification/SubjectRegisterPage.js').then((m) => ({
    default: m.SubjectRegisterPage,
  })),
);
const ClassificationSubjectProfileFormPage = lazy(() =>
  import('./pages/admin/regulatory-classification/SubjectProfileFormPage.js').then((m) => ({
    default: m.SubjectProfileFormPage,
  })),
);
const ClassificationAssessmentPage = lazy(() =>
  import('./pages/admin/regulatory-classification/ClassificationAssessmentPage.js').then((m) => ({
    default: m.ClassificationAssessmentPage,
  })),
);
const ClassificationSubjectDetailPage = lazy(() =>
  import('./pages/admin/regulatory-classification/SubjectProfileDetailPage.js').then((m) => ({
    default: m.SubjectProfileDetailPage,
  })),
);
const RegulatedOfferingsPage = lazy(() =>
  import('./pages/admin/regulatory-classification/RegulatedOfferingsPage.js').then((m) => ({
    default: m.RegulatedOfferingsPage,
  })),
);
const RegulatedOfferingFormPage = lazy(() =>
  import('./pages/admin/regulatory-classification/RegulatedOfferingFormPage.js').then((m) => ({
    default: m.RegulatedOfferingFormPage,
  })),
);
// Sprint 62.13.0 — penalty exposure + enforcement cases (decree-wave stream).
const PenaltyReadinessPage = lazy(() =>
  import('./pages/admin/penalty-exposure/PenaltyReadinessPage.js').then((m) => ({
    default: m.PenaltyReadinessPage,
  })),
);
const ExposureInputsPage = lazy(() =>
  import('./pages/admin/penalty-exposure/ExposureInputsPage.js').then((m) => ({
    default: m.ExposureInputsPage,
  })),
);
// Sprint 62.35.0 (Slice A) — the enforcement register.
const EnforcementRegisterPage = lazy(() =>
  import('./pages/admin/penalty-exposure/EnforcementRegisterPage.js').then((m) => ({
    default: m.EnforcementRegisterPage,
  })),
);
const EnforcementCasesPage = lazy(() =>
  import('./pages/admin/penalty-exposure/EnforcementCasesPage.js').then((m) => ({
    default: m.EnforcementCasesPage,
  })),
);
const EnforcementCaseDetailPage = lazy(() =>
  import('./pages/admin/penalty-exposure/EnforcementCaseDetailPage.js').then((m) => ({
    default: m.EnforcementCaseDetailPage,
  })),
);
// Sprint 62.14.0 — authority directions, legal holds, regulated accounts.
const DirectionsPage = lazy(() =>
  import('./pages/admin/authority-operations/DirectionsPage.js').then((m) => ({
    default: m.DirectionsPage,
  })),
);
const DirectionDetailPage = lazy(() =>
  import('./pages/admin/authority-operations/DirectionDetailPage.js').then((m) => ({
    default: m.DirectionDetailPage,
  })),
);
const LegalHoldsPage = lazy(() =>
  import('./pages/admin/authority-operations/LegalHoldsPage.js').then((m) => ({
    default: m.LegalHoldsPage,
  })),
);
const RegulatedAccountsPage = lazy(() =>
  import('./pages/admin/authority-operations/RegulatedAccountsPage.js').then((m) => ({
    default: m.RegulatedAccountsPage,
  })),
);
const RegulatedAccountDetailPage = lazy(() =>
  import('./pages/admin/authority-operations/RegulatedAccountDetailPage.js').then((m) => ({
    default: m.RegulatedAccountDetailPage,
  })),
);
// Sprint 62.22.0 — pack-fed product position and conformity evidence.
const ProductClassificationsPage = lazy(() =>
  import('./pages/admin/product-assurance/ProductClassificationsPage.js').then((m) => ({
    default: m.ProductClassificationsPage,
  })),
);
const ConformityCertificatesPage = lazy(() =>
  import('./pages/admin/product-assurance/ConformityCertificatesPage.js').then((m) => ({
    default: m.ConformityCertificatesPage,
  })),
);
// Sprint 62.19.0 — sector licences, the licence detail and the marketing-claim
// gate, then the three investor-protection registers and the disclosure detail.
// Sprint 62.20.0 — the regulatory placeholder register.
const RegulatoryPlaceholdersPage = lazy(() =>
  import('./pages/admin/regulatory-placeholders/RegulatoryPlaceholdersPage.js').then((m) => ({
    default: m.RegulatoryPlaceholdersPage,
  })),
);
const RegulatoryPlaceholderDetailPage = lazy(() =>
  import('./pages/admin/regulatory-placeholders/RegulatoryPlaceholderDetailPage.js').then((m) => ({
    default: m.RegulatoryPlaceholderDetailPage,
  })),
);
const SectorLicencesPage = lazy(() =>
  import('./pages/admin/sector-licensing/SectorLicencesPage.js').then((m) => ({
    default: m.SectorLicencesPage,
  })),
);
// Sprint 62.24.0 — consent evidence by recipient class and the credit-information supply register.
// Sprint 62.28.0 — the three marketing-communications registers.
const MarketingConsentPage = lazy(
  () => import('./pages/admin/marketing-communications/MarketingConsentPage.js'),
);
const SuppressionRegisterPage = lazy(
  () => import('./pages/admin/marketing-communications/SuppressionRegisterPage.js'),
);
const SuppressionEnforcementPage = lazy(
  () => import('./pages/admin/marketing-communications/SuppressionEnforcementPage.js'),
);
// Sprint 62.54.0 — sender-name certificates and their charge lines (sender-identity side).
const SenderNameCertificatesPage = lazy(
  () => import('./pages/admin/marketing-communications/SenderNameCertificatesPage.js'),
);
// Sprint 62.54.0 — licence, certificate and appraisal applications with their fee lines.
const LicenceApplicationsPage = lazy(
  () => import('./pages/admin/sector-licensing/LicenceApplicationsPage.js'),
);
const LicenceApplicationDetailPage = lazy(
  () => import('./pages/admin/sector-licensing/LicenceApplicationDetailPage.js'),
);
// Sprint 62.55.0 (UC-VN-109 F-3) — the fee and charge lines of one paid-on month.
const FeeLinesByPeriodPage = lazy(
  () => import('./pages/admin/sector-licensing/FeeLinesByPeriodPage.js'),
);
const ConsentEvidencePage = lazy(() =>
  import('./pages/admin/consent/ConsentEvidencePage.js').then((m) => ({
    default: m.ConsentEvidencePage,
  })),
);
const CreditInformationSuppliesPage = lazy(() =>
  import('./pages/admin/credit-information-supply/CreditInformationSuppliesPage.js').then((m) => ({
    default: m.CreditInformationSuppliesPage,
  })),
);
const CreditInformationSupplyDetailPage = lazy(() =>
  import('./pages/admin/credit-information-supply/CreditInformationSupplyDetailPage.js').then(
    (m) => ({ default: m.CreditInformationSupplyDetailPage }),
  ),
);
// Sprint 62.24.0 — the data-service provider record.
const DataServiceProvidersPage = lazy(() =>
  import('./pages/admin/data-services/DataServiceProvidersPage.js').then((m) => ({
    default: m.DataServiceProvidersPage,
  })),
);
const DataServiceProviderDetailPage = lazy(() =>
  import('./pages/admin/data-services/DataServiceProviderDetailPage.js').then((m) => ({
    default: m.DataServiceProviderDetailPage,
  })),
);
// Sprint 62.50.0 — the supervision-information registers (Localization › Sector Overlays).
const PcfSharedInformationPage = lazy(() =>
  import('./pages/admin/pcf-supervision/SharedInformationPage.js').then((m) => ({
    default: m.SharedInformationPage,
  })),
);
const PcfBranchPublicityPage = lazy(() =>
  import('./pages/admin/pcf-supervision/BranchPublicityPage.js').then((m) => ({
    default: m.BranchPublicityPage,
  })),
);
const PcfBranchInformationRequestsPage = lazy(() =>
  import('./pages/admin/pcf-supervision/BranchInformationRequestsPage.js').then((m) => ({
    default: m.BranchInformationRequestsPage,
  })),
);
const PcfDepositorEnquiriesPage = lazy(() =>
  import('./pages/admin/pcf-supervision/DepositorEnquiriesPage.js').then((m) => ({
    default: m.DepositorEnquiriesPage,
  })),
);
const PcfSupervisionSystemRecordsPage = lazy(() =>
  import('./pages/admin/pcf-supervision/SupervisionSystemRecordsPage.js').then((m) => ({
    default: m.SupervisionSystemRecordsPage,
  })),
);
// Sprint 62.51.0 — the data-exploitation registers (Localization › Data Exploitation).
const DeExploitationRoutesPage = lazy(() =>
  import('./pages/admin/data-exploitation/ExploitationRoutesPage.js').then((m) => ({
    default: m.ExploitationRoutesPage,
  })),
);
const DeRequestsGrantsPage = lazy(() =>
  import('./pages/admin/data-exploitation/RequestsGrantsPage.js').then((m) => ({
    default: m.RequestsGrantsPage,
  })),
);
const DeTestingRecordsPage = lazy(() =>
  import('./pages/admin/data-exploitation/TestingRecordsPage.js').then((m) => ({
    default: m.TestingRecordsPage,
  })),
);
const DeIncidentNoticesPage = lazy(() =>
  import('./pages/admin/data-exploitation/IncidentNoticesPage.js').then((m) => ({
    default: m.IncidentNoticesPage,
  })),
);
const DeDispositionsPage = lazy(() =>
  import('./pages/admin/data-exploitation/DispositionsPage.js').then((m) => ({
    default: m.DispositionsPage,
  })),
);
const DeIncubatorRecordsPage = lazy(() =>
  import('./pages/admin/data-exploitation/IncubatorRecordsPage.js').then((m) => ({
    default: m.IncubatorRecordsPage,
  })),
);
const DeForeignCooperationPage = lazy(() =>
  import('./pages/admin/data-exploitation/ForeignCooperationPage.js').then((m) => ({
    default: m.ForeignCooperationPage,
  })),
);
// Sprint 62.52.0 — the cybersecurity assurance index registers (Localization › Cybersecurity Assurance Index).
const CsaiPeriodsPage = lazy(() =>
  import('./pages/admin/cybersecurity-assurance-index/PeriodsPage.js').then((m) => ({
    default: m.PeriodsPage,
  })),
);
const CsaiPeriodDetailPage = lazy(() =>
  import('./pages/admin/cybersecurity-assurance-index/PeriodDetailPage.js').then((m) => ({
    default: m.PeriodDetailPage,
  })),
);
const CsaiSystemItemsPage = lazy(() =>
  import('./pages/admin/cybersecurity-assurance-index/SystemItemsPage.js').then((m) => ({
    default: m.SystemItemsPage,
  })),
);
const CsaiStatisticsPage = lazy(() =>
  import('./pages/admin/cybersecurity-assurance-index/StatisticsPage.js').then((m) => ({
    default: m.StatisticsPage,
  })),
);
const CsaiSupplierRequestsPage = lazy(() =>
  import('./pages/admin/cybersecurity-assurance-index/SupplierRequestsPage.js').then((m) => ({
    default: m.SupplierRequestsPage,
  })),
);
// Sprint 62.49.0 — the food traceability registers (Localization › Food Traceability).
const FtEstablishmentPage = lazy(() =>
  import('./pages/admin/food-traceability/EstablishmentPage.js').then((m) => ({
    default: m.EstablishmentPage,
  })),
);
const FtLotResultsPage = lazy(() =>
  import('./pages/admin/food-traceability/LotResultsPage.js').then((m) => ({
    default: m.LotResultsPage,
  })),
);
const FtInspectionsPage = lazy(() =>
  import('./pages/admin/food-traceability/InspectionsPage.js').then((m) => ({
    default: m.InspectionsPage,
  })),
);
const FtWarningsPage = lazy(() =>
  import('./pages/admin/food-traceability/WarningsPage.js').then((m) => ({
    default: m.WarningsPage,
  })),
);
const FtNoticesPage = lazy(() =>
  import('./pages/admin/food-traceability/NoticesPage.js').then((m) => ({
    default: m.NoticesPage,
  })),
);
const FtDecisionsPage = lazy(() =>
  import('./pages/admin/food-traceability/DecisionsPage.js').then((m) => ({
    default: m.DecisionsPage,
  })),
);
const FtCorrectionsPage = lazy(() =>
  import('./pages/admin/food-traceability/CorrectionsPage.js').then((m) => ({
    default: m.CorrectionsPage,
  })),
);
const FtComplaintsPage = lazy(() =>
  import('./pages/admin/food-traceability/ComplaintsPage.js').then((m) => ({
    default: m.ComplaintsPage,
  })),
);
const FtConnectionRoutesPage = lazy(() =>
  import('./pages/admin/food-traceability/ConnectionRoutesPage.js').then((m) => ({
    default: m.ConnectionRoutesPage,
  })),
);
// Sprint 62.56.0 — the biometric capture registers (Localization › Sector Overlays).
const BiometricCaptureComponentsPage = lazy(() =>
  import('./pages/admin/biometric-capture/BiometricCaptureComponentsPage.js').then((m) => ({
    default: m.BiometricCaptureComponentsPage,
  })),
);
const BiometricCaptureComponentDetailPage = lazy(() =>
  import('./pages/admin/biometric-capture/BiometricCaptureComponentDetailPage.js').then((m) => ({
    default: m.BiometricCaptureComponentDetailPage,
  })),
);
const BiometricCustodyTransfersPage = lazy(() =>
  import('./pages/admin/biometric-capture/BiometricCustodyTransfersPage.js').then((m) => ({
    default: m.BiometricCustodyTransfersPage,
  })),
);
const BiometricSupervisionRecordsPage = lazy(() =>
  import('./pages/admin/biometric-capture/BiometricSupervisionRecordsPage.js').then((m) => ({
    default: m.BiometricSupervisionRecordsPage,
  })),
);
const BiometricReadinessReviewsPage = lazy(() =>
  import('./pages/admin/biometric-capture/BiometricReadinessReviewsPage.js').then((m) => ({
    default: m.BiometricReadinessReviewsPage,
  })),
);
// Sprint 63.24.0 Slice A — controlled-testing cases (the admission case and its lifecycle).
const CaseListPage = lazy(() =>
  import('./pages/admin/controlled-testing/CaseListPage.js').then((m) => ({
    default: m.CaseListPage,
  })),
);
const CaseDetailPage = lazy(() =>
  import('./pages/admin/controlled-testing/CaseDetailPage.js').then((m) => ({
    default: m.CaseDetailPage,
  })),
);
const SectorLicenceDetailPage = lazy(() =>
  import('./pages/admin/sector-licensing/SectorLicenceDetailPage.js').then((m) => ({
    default: m.SectorLicenceDetailPage,
  })),
);
const MarketingApprovalsPage = lazy(() =>
  import('./pages/admin/sector-licensing/MarketingApprovalsPage.js').then((m) => ({
    default: m.MarketingApprovalsPage,
  })),
);

// Sprint 62.19.0 — investor verifications, account-data disclosures,
// regulatory disclosures (module `investor_protection`).
const InvestorVerificationsPage = lazy(() =>
  import('./pages/admin/investor-protection/InvestorVerificationsPage.js').then((m) => ({
    default: m.InvestorVerificationsPage,
  })),
);
const AccountDataDisclosuresPage = lazy(() =>
  import('./pages/admin/investor-protection/AccountDataDisclosuresPage.js').then((m) => ({
    default: m.AccountDataDisclosuresPage,
  })),
);
const RegulatoryDisclosuresPage = lazy(() =>
  import('./pages/admin/investor-protection/RegulatoryDisclosuresPage.js').then((m) => ({
    default: m.RegulatoryDisclosuresPage,
  })),
);
const RegulatoryDisclosureDetailPage = lazy(() =>
  import('./pages/admin/investor-protection/RegulatoryDisclosureDetailPage.js').then((m) => ({
    default: m.RegulatoryDisclosureDetailPage,
  })),
);
// Sprint 65.1.0 — the DPO work programme: the obligation register and one
// duty's detail (module `compliance_operations`).
const ObligationListPage = lazy(() =>
  import('./pages/admin/compliance-operations/ObligationListPage.js').then((m) => ({
    default: m.ObligationListPage,
  })),
);
const ObligationDetailPage = lazy(() =>
  import('./pages/admin/compliance-operations/ObligationDetailPage.js').then((m) => ({
    default: m.ObligationDetailPage,
  })),
);
// Sprint 62.18.0 — the issued-record register (Student Records) and the
// learner-record detail (module `issued_records`).
const StudentRecordsPage = lazy(() =>
  import('./pages/admin/issued-records/StudentRecordsPage.js').then((m) => ({
    default: m.StudentRecordsPage,
  })),
);
const StudentRecordDetailPage = lazy(() =>
  import('./pages/admin/issued-records/StudentRecordDetailPage.js').then((m) => ({
    default: m.StudentRecordDetailPage,
  })),
);
// Sprint 62.17.0 — publishing: the publication register and one title's
// detail (deposit submissions, the release gate, categories, rights evidence),
// the append-only appointed-representative register, and the AI-assisted
// publication assessment.
const PublicationsPage = lazy(() =>
  import('./pages/admin/publishing/PublicationsPage.js').then((m) => ({
    default: m.PublicationsPage,
  })),
);
const PublicationDetailPage = lazy(() =>
  import('./pages/admin/publishing/PublicationDetailPage.js').then((m) => ({
    default: m.PublicationDetailPage,
  })),
);
const LegalRepresentativesPage = lazy(() =>
  import('./pages/admin/publishing/LegalRepresentativesPage.js').then((m) => ({
    default: m.LegalRepresentativesPage,
  })),
);
const PublicationAssessmentsPage = lazy(() =>
  import('./pages/admin/publishing/PublicationAssessmentsPage.js').then((m) => ({
    default: m.PublicationAssessmentsPage,
  })),
);
// Sprint 62.5.0 — trust evidence: the identity register, trust providers and
// credentials, electronic submissions and electronic agreements, each with its
// own detail surface.
const TrustIdentityRegisterPage = lazy(() =>
  import('./pages/admin/trust-evidence/IdentityRegisterPage.js').then((m) => ({
    default: m.IdentityRegisterPage,
  })),
);
const TrustIdentityProfileDetailPage = lazy(() =>
  import('./pages/admin/trust-evidence/IdentityProfileDetailPage.js').then((m) => ({
    default: m.IdentityProfileDetailPage,
  })),
);
const TrustProvidersPage = lazy(() =>
  import('./pages/admin/trust-evidence/TrustProvidersPage.js').then((m) => ({
    default: m.TrustProvidersPage,
  })),
);
const TrustCredentialDetailPage = lazy(() =>
  import('./pages/admin/trust-evidence/CredentialDetailPage.js').then((m) => ({
    default: m.CredentialDetailPage,
  })),
);
const ElectronicSubmissionsPage = lazy(() =>
  import('./pages/admin/trust-evidence/ElectronicSubmissionsPage.js').then((m) => ({
    default: m.ElectronicSubmissionsPage,
  })),
);
const ElectronicSubmissionDetailPage = lazy(() =>
  import('./pages/admin/trust-evidence/SubmissionDetailPage.js').then((m) => ({
    default: m.SubmissionDetailPage,
  })),
);
const ElectronicAgreementsPage = lazy(() =>
  import('./pages/admin/trust-evidence/ElectronicAgreementsPage.js').then((m) => ({
    default: m.ElectronicAgreementsPage,
  })),
);
const ElectronicAgreementDetailPage = lazy(() =>
  import('./pages/admin/trust-evidence/AgreementDetailPage.js').then((m) => ({
    default: m.AgreementDetailPage,
  })),
);
// Sprint 62.6.0 — regulatory artifacts: what an artifact is, and who may see
// it. Three leaves plus a detail surface with no leaf of its own.
const ArtifactCatalogPage = lazy(() =>
  import('./pages/admin/regulatory-artifacts/ArtifactCatalogPage.js').then((m) => ({
    default: m.ArtifactCatalogPage,
  })),
);
const ArtifactRestrictedAccessPage = lazy(() =>
  import('./pages/admin/regulatory-artifacts/RestrictedAccessPage.js').then((m) => ({
    default: m.RestrictedAccessPage,
  })),
);
const ArtifactVerificationQueuePage = lazy(() =>
  import('./pages/admin/regulatory-artifacts/VerificationQueuePage.js').then((m) => ({
    default: m.VerificationQueuePage,
  })),
);
const ArtifactDetailPage = lazy(() =>
  import('./pages/admin/regulatory-artifacts/ArtifactDetailPage.js').then((m) => ({
    default: m.ArtifactDetailPage,
  })),
);
// Sprint 62.7.0 — public data exchange, payments & eKYC evidence, and platform
// evidence. Ten leaves across three groups plus two detail surfaces that get no
// leaf of their own.
// ── Sprint 62.8.0 — manufacturing operations ────────────────────────
//
// Nine leaves under two existing modules. Default exports, so no `.then` shim is
// needed — the 62.7.0 pages use named exports and pay for it in nine lines each.
const ManufacturingProductRegisterPage = lazy(
  () => import('./pages/admin/manufacturing/ProductRegisterPage.js'),
);
const ManufacturingBatchRegisterPage = lazy(
  () => import('./pages/admin/manufacturing/BatchRegisterPage.js'),
);
const ManufacturingTraceTimelinePage = lazy(
  () => import('./pages/admin/manufacturing/TraceTimelinePage.js'),
);
const ManufacturingConformityEvidencePage = lazy(
  () => import('./pages/admin/manufacturing/ConformityEvidencePage.js'),
);
const ManufacturingCapaRecallPage = lazy(
  () => import('./pages/admin/manufacturing/CapaRecallPage.js'),
);
const ManufacturingEprReconciliationPage = lazy(
  () => import('./pages/admin/manufacturing/EprReconciliationPage.js'),
);
const ManufacturingChemicalRegisterPage = lazy(
  () => import('./pages/admin/manufacturing/ChemicalRegisterPage.js'),
);
// Sprint 63.31.0 (#444, #445) — the chemical traceability declarations view.
const ManufacturingChemicalTraceabilityPage = lazy(
  () => import('./pages/admin/manufacturing/ChemicalTraceabilityDeclarationsPage.js'),
);
const ManufacturingOtAssetProfilesPage = lazy(
  () => import('./pages/admin/manufacturing/OtAssetProfilesPage.js'),
);
const ManufacturingRemoteAccessAssurancePage = lazy(
  () => import('./pages/admin/manufacturing/RemoteAccessAssurancePage.js'),
);

// ── Sprint 62.9.0 — logistics, customs, parcel and delivery ─────────
//
// Seven leaves under **one** existing module (Program Governance), in two
// sub-groups. The route prefix follows the API namespace — one mount at
// `/api/v1/logistics` — rather than the sidebar module, which is the 62.8.0
// convention. Default exports, so no `.then` shim is needed.
const LogisticsShipmentRegisterPage = lazy(
  () => import('./pages/admin/logistics/ShipmentRegisterPage.js'),
);
const LogisticsCustomsEvidencePage = lazy(
  () => import('./pages/admin/logistics/CustomsEvidencePage.js'),
);
const LogisticsInspectionsHoldsPage = lazy(
  () => import('./pages/admin/logistics/InspectionsHoldsPage.js'),
);
const LogisticsParcelRegisterPage = lazy(
  () => import('./pages/admin/logistics/ParcelRegisterPage.js'),
);
const LogisticsDeliveryEvidencePage = lazy(
  () => import('./pages/admin/logistics/DeliveryEvidencePage.js'),
);
const LogisticsClaimsPage = lazy(() => import('./pages/admin/logistics/ClaimsPage.js'));
const LogisticsPostalAuthorisationsPage = lazy(
  () => import('./pages/admin/logistics/PostalAuthorisationsPage.js'),
);
// Sprint 62.55.0 — the customs tax-administration registers (Logistics › Trade &
// Customs Evidence). Named exports, so the `.then` shim maps each to `default`.
const CustomsTaxDossiersPage = lazy(() =>
  import('./pages/admin/customs-tax-administration/TaxDossiersPage.js').then((m) => ({
    default: m.TaxDossiersPage,
  })),
);
const CustomsTaxPaymentsPage = lazy(() =>
  import('./pages/admin/customs-tax-administration/TaxPaymentsPage.js').then((m) => ({
    default: m.TaxPaymentsPage,
  })),
);
const CustomsTaxDutyFreeListsPage = lazy(() =>
  import('./pages/admin/customs-tax-administration/DutyFreeListsPage.js').then((m) => ({
    default: m.DutyFreeListsPage,
  })),
);
const CustomsTaxExaminationsPage = lazy(() =>
  import('./pages/admin/customs-tax-administration/TaxExaminationsPage.js').then((m) => ({
    default: m.TaxExaminationsPage,
  })),
);
const CustomsTaxTransitionPage = lazy(() =>
  import('./pages/admin/customs-tax-administration/TaxTransitionPage.js').then((m) => ({
    default: m.TaxTransitionPage,
  })),
);

const PublicDataDatasetCatalogPage = lazy(() =>
  import('./pages/admin/public-data/DatasetCatalogPage.js').then((m) => ({
    default: m.DatasetCatalogPage,
  })),
);
const PublicDataDatasetDetailPage = lazy(() =>
  import('./pages/admin/public-data/DatasetDetailPage.js').then((m) => ({
    default: m.DatasetDetailPage,
  })),
);
const PublicDataSharingRegisterPage = lazy(() =>
  import('./pages/admin/public-data/SharingRegisterPage.js').then((m) => ({
    default: m.SharingRegisterPage,
  })),
);
const PublicDataOpenDataReleasesPage = lazy(() =>
  import('./pages/admin/public-data/OpenDataReleasesPage.js').then((m) => ({
    default: m.OpenDataReleasesPage,
  })),
);
const PaymentServicesPage = lazy(() =>
  import('./pages/admin/payments-evidence/PaymentServicesPage.js').then((m) => ({
    default: m.PaymentServicesPage,
  })),
);
const EkycEvidencePage = lazy(() =>
  import('./pages/admin/payments-evidence/EkycEvidencePage.js').then((m) => ({
    default: m.EkycEvidencePage,
  })),
);
const EkycVerificationDetailPage = lazy(() =>
  import('./pages/admin/payments-evidence/VerificationDetailPage.js').then((m) => ({
    default: m.VerificationDetailPage,
  })),
);
const PaymentExceptionReviewPage = lazy(() =>
  import('./pages/admin/payments-evidence/ExceptionReviewPage.js').then((m) => ({
    default: m.ExceptionReviewPage,
  })),
);
const RetentionReconciliationPage = lazy(() =>
  import('./pages/admin/payments-evidence/RetentionReconciliationPage.js').then((m) => ({
    default: m.RetentionReconciliationPage,
  })),
);
// Sprint 62.38.0 — the Supervisory Reporting register.
const SupervisoryReportingPage = lazy(() =>
  import('./pages/admin/payments-evidence/SupervisoryReportingPage.js').then((m) => ({
    default: m.SupervisoryReportingPage,
  })),
);
// Sprint 62.46.0 — the AML programme register and the restricted case register.
const AmlProgrammePage = lazy(() =>
  import('./pages/admin/payments-evidence/AmlProgrammePage.js').then((m) => ({
    default: m.AmlProgrammePage,
  })),
);
const AmlCasesPage = lazy(() =>
  import('./pages/admin/payments-evidence/AmlCasesPage.js').then((m) => ({
    default: m.AmlCasesPage,
  })),
);
const AmlCaseDetailPage = lazy(() =>
  import('./pages/admin/payments-evidence/AmlCaseDetailPage.js').then((m) => ({
    default: m.AmlCaseDetailPage,
  })),
);
// Sprint 62.39.0 — the Cross-Border Identity Exchange register (elective).
const IdentityExchangePage = lazy(() =>
  import('./pages/admin/trust-evidence/IdentityExchangePage.js').then((m) => ({
    default: m.IdentityExchangePage,
  })),
);
const PlatformSellerRegisterPage = lazy(() =>
  import('./pages/admin/platform-evidence/SellerRegisterPage.js').then((m) => ({
    default: m.SellerRegisterPage,
  })),
);
const PlatformTransactionReconciliationPage = lazy(() =>
  import('./pages/admin/platform-evidence/TransactionReconciliationPage.js').then((m) => ({
    default: m.TransactionReconciliationPage,
  })),
);
const PlatformAuthorityCorrespondencePage = lazy(() =>
  import('./pages/admin/platform-evidence/AuthorityCorrespondencePage.js').then((m) => ({
    default: m.AuthorityCorrespondencePage,
  })),
);
const AuditEvidencePacksPage = lazy(() =>
  import('./pages/admin/audit/EvidencePacksPage.js').then((m) => ({
    default: m.EvidencePacksPage,
  })),
);
const AuditPeriodsPage = lazy(() =>
  import('./pages/admin/audit/AuditPeriodsPage.js').then((m) => ({
    default: m.AuditPeriodsPage,
  })),
);
// Sprint 43.5.1 consolidation — TemplatesPage (Sprint 1.1.0 Epic C.1) removed.
// Replaced by ResponseTemplatesAdminPage at `/admin/rights-requests/response-templates`
// backed by the canonical DsrResponseTemplate system. The old `/admin/templates`
// route has been removed; the lazy import is dropped along with the page file.
// Sprint 1.2.0 Data Mapping pages
const SystemsPage = lazy(() =>
  import('./pages/admin/data-mapping/SystemsPage.js').then((m) => ({ default: m.SystemsPage })),
);
const CategoriesPage = lazy(() =>
  import('./pages/admin/data-mapping/CategoriesPage.js').then((m) => ({
    default: m.CategoriesPage,
  })),
);
// Sprint 45.4.0 — Data Registry sub-module.
const RegistryPage = lazy(() =>
  import('./pages/admin/data-mapping/RegistryPage.js').then((m) => ({
    default: m.RegistryPage,
  })),
);
const PurposesPage = lazy(() =>
  import('./pages/admin/data-mapping/PurposesPage.js').then((m) => ({ default: m.PurposesPage })),
);
const RetentionPage = lazy(() =>
  import('./pages/admin/data-mapping/RetentionPage.js').then((m) => ({ default: m.RetentionPage })),
);
const FlowsPage = lazy(() =>
  import('./pages/admin/data-mapping/FlowsPage.js').then((m) => ({ default: m.FlowsPage })),
);
const ActivitiesPage = lazy(() =>
  import('./pages/admin/data-mapping/ActivitiesPage.js').then((m) => ({
    default: m.ActivitiesPage,
  })),
);
// Sprint 46.0.0 — ROPA Completeness Report
const RopaCompletenessPage = lazy(() =>
  import('./pages/admin/data-mapping/RopaCompletenessPage.js').then((m) => ({
    default: m.RopaCompletenessPage,
  })),
);
const EvidencePage = lazy(() =>
  import('./pages/admin/data-mapping/EvidencePage.js').then((m) => ({ default: m.EvidencePage })),
);
const ExportsPage = lazy(() =>
  import('./pages/admin/data-mapping/ExportsPage.js').then((m) => ({ default: m.ExportsPage })),
);
// Sprint 20.5.0 Data Mapping Maturity pages
const AttestationCyclePage = lazy(() =>
  import('./pages/admin/data-mapping/AttestationCyclePage.js').then((m) => ({
    default: m.AttestationCyclePage,
  })),
);
const GovernanceAssignmentsPage = lazy(() =>
  import('./pages/admin/data-mapping/GovernanceAssignmentsPage.js').then((m) => ({
    default: m.GovernanceAssignmentsPage,
  })),
);
// Sprint 27.8.1 Data Discovery Workflow pages
const DataMappingReviewPage = lazy(() =>
  import('./pages/admin/data-mapping/DataMappingReviewPage.js').then((m) => ({
    default: m.DataMappingReviewPage,
  })),
);
const DataMappingInventoryPage = lazy(() =>
  import('./pages/admin/data-mapping/DataMappingInventoryPage.js').then((m) => ({
    default: m.DataMappingInventoryPage,
  })),
);
const DataMappingExceptionsPage = lazy(() =>
  import('./pages/admin/data-mapping/DataMappingExceptionsPage.js').then((m) => ({
    default: m.DataMappingExceptionsPage,
  })),
);
const DataMappingHistoryPage = lazy(() =>
  import('./pages/admin/data-mapping/DataMappingHistoryPage.js').then((m) => ({
    default: m.DataMappingHistoryPage,
  })),
);
// Sprint 1.3.0 Governance pages
const VendorsPage = lazy(() =>
  import('./pages/admin/governance/VendorsPage.js').then((m) => ({ default: m.VendorsPage })),
);
const SubProcessorsPage = lazy(() =>
  import('./pages/admin/governance/SubProcessorsPage.js').then((m) => ({
    default: m.SubProcessorsPage,
  })),
);
const CrossBorderPage = lazy(() =>
  import('./pages/admin/governance/CrossBorderPage.js').then((m) => ({
    default: m.CrossBorderPage,
  })),
);
const CrossBorderFilingDetailPage = lazy(() =>
  import('./pages/admin/governance/CrossBorderFilingDetailPage.js').then((m) => ({
    default: m.CrossBorderFilingDetailPage,
  })),
);
const DriftMonitorPage = lazy(() =>
  import('./pages/admin/governance/DriftMonitorPage.js').then((m) => ({
    default: m.DriftMonitorPage,
  })),
);
const ComplianceScorePage = lazy(() =>
  import('./pages/admin/governance/ComplianceScorePage.js').then((m) => ({
    default: m.ComplianceScorePage,
  })),
);
const PlaybooksPage = lazy(() =>
  import('./pages/admin/governance/PlaybooksPage.js').then((m) => ({ default: m.PlaybooksPage })),
);
const ApprovalsPage = lazy(() =>
  import('./pages/admin/governance/ApprovalsPage.js').then((m) => ({ default: m.ApprovalsPage })),
);
const GovernanceEvidencePage = lazy(() =>
  import('./pages/admin/governance/GovernanceEvidencePage.js').then((m) => ({
    default: m.GovernanceEvidencePage,
  })),
);
// Sprint 41.2.0 E6 — Governance Audit Trail page
const GovernanceAuditPage = lazy(() =>
  import('./pages/admin/governance/GovernanceAuditPage.js').then((m) => ({
    default: m.GovernanceAuditPage,
  })),
);
// Sprint 18.2.0 Board Report
const BoardReportPage = lazy(() =>
  import('./pages/admin/governance/BoardReportPage.js').then((m) => ({
    default: m.BoardReportPage,
  })),
);
// Sprint 41.5.0 G4 — OnboardingPipelinePage and DpaReviewPage were
// deleted in favor of panels embedded on vendor-risk/VendorOnboardingCasePage.
// The old route paths stay mapped for bookmark continuity via
// DeprecatedGovernanceRedirect.
import { DeprecatedGovernanceRedirect } from './components/DeprecatedGovernanceRedirect.js';
const PeriodicReviewPage = lazy(() =>
  import('./pages/admin/governance/PeriodicReviewPage.js').then((m) => ({
    default: m.PeriodicReviewPage,
  })),
);
const OffboardingPage = lazy(() =>
  import('./pages/admin/governance/OffboardingPage.js').then((m) => ({
    default: m.OffboardingPage,
  })),
);
// Sprint 1.4.0 Consent pages
const PoliciesPage = lazy(() =>
  import('./pages/admin/consent/PoliciesPage.js').then((m) => ({ default: m.PoliciesPage })),
);
const PurposeCatalogPage = lazy(() =>
  import('./pages/admin/consent/PurposeCatalogPage.js').then((m) => ({
    default: m.PurposeCatalogPage,
  })),
);
// Sprint 48.1.4 — Consent gap analysis Forseti workflow page
const ConsentGapAnalysisPage = lazy(() =>
  import('./pages/admin/consent/ConsentGapAnalysisPage.js').then((m) => ({
    default: m.ConsentGapAnalysisPage,
  })),
);
const WorkflowsPage = lazy(() =>
  import('./pages/admin/consent/WorkflowsPage.js').then((m) => ({ default: m.WorkflowsPage })),
);
const SnapshotsPage = lazy(() =>
  import('./pages/admin/consent/SnapshotsPage.js').then((m) => ({ default: m.SnapshotsPage })),
);
const ChangeAwarenessPage = lazy(() =>
  import('./pages/admin/consent/ChangeAwarenessPage.js').then((m) => ({
    default: m.ChangeAwarenessPage,
  })),
);
const ConsentAuditPage = lazy(() =>
  import('./pages/admin/consent/ConsentAuditPage.js').then((m) => ({
    default: m.ConsentAuditPage,
  })),
);
// Sprint 4.2.0 CMP Consent Integration pages
const CmpIntegrationPage = lazy(() =>
  import('./pages/admin/consent/cmp-integration/CmpIntegrationPage.js').then((m) => ({
    default: m.CmpIntegrationPage,
  })),
);
const WebhookConfigPage = lazy(() =>
  import('./pages/admin/consent/cmp-integration/WebhookConfigPage.js').then((m) => ({
    default: m.WebhookConfigPage,
  })),
);
const PurposeMappingPage = lazy(() =>
  import('./pages/admin/consent/cmp-integration/PurposeMappingPage.js').then((m) => ({
    default: m.PurposeMappingPage,
  })),
);
const BulkSyncPage = lazy(() =>
  import('./pages/admin/consent/cmp-integration/BulkSyncPage.js').then((m) => ({
    default: m.BulkSyncPage,
  })),
);
// Sprint 20.6.0 Consent Governance pages
const WithdrawalPropagationPage = lazy(() =>
  import('./pages/admin/consent/WithdrawalPropagationPage.js').then((m) => ({
    default: m.WithdrawalPropagationPage,
  })),
);
const ReconciliationPage = lazy(() =>
  import('./pages/admin/consent/ReconciliationPage.js').then((m) => ({
    default: m.ReconciliationPage,
  })),
);
const CmpSyncPage = lazy(() =>
  import('./pages/admin/consent/CmpSyncPage.js').then((m) => ({ default: m.CmpSyncPage })),
);
// Sprint 40.0.0 Import pages
const ImportWorkflowsPage = lazy(() =>
  import('./pages/admin/consent/ImportWorkflowsPage.js').then((m) => ({
    default: m.ImportWorkflowsPage,
  })),
);
const ImportCasesPage = lazy(() =>
  import('./pages/admin/rights-requests/ImportCasesPage.js').then((m) => ({
    default: m.ImportCasesPage,
  })),
);
// Sprint 48.1.1 (finish) — DSR Batch Upload + Forseti AI workflow.
const DsrBatchUploadPage = lazy(() =>
  import('./pages/admin/rights-requests/DsrBatchUploadPage.js').then((m) => ({
    default: m.DsrBatchUploadPage,
  })),
);
// Sprint 40.2.0 Data Subject Profile pages
const DataSubjectListPage = lazy(() =>
  import('./pages/admin/data-subjects/DataSubjectListPage.js').then((m) => ({
    default: m.DataSubjectListPage,
  })),
);
const DataSubjectDetailPage = lazy(() =>
  import('./pages/admin/data-subjects/DataSubjectDetailPage.js').then((m) => ({
    default: m.DataSubjectDetailPage,
  })),
);
// Sprint 1.5.0 Assessment pages
const DpiaListPage = lazy(() =>
  import('./pages/admin/assessments/DpiaListPage.js').then((m) => ({ default: m.DpiaListPage })),
);
const TransferListPage = lazy(() =>
  import('./pages/admin/assessments/TransferListPage.js').then((m) => ({
    default: m.TransferListPage,
  })),
);
const AssessmentDashboardPage = lazy(() =>
  import('./pages/admin/assessments/DashboardPage.js').then((m) => ({ default: m.DashboardPage })),
);
const DossierFilingListPage = lazy(() =>
  import('./pages/admin/assessments/DossierFilingListPage.js').then((m) => ({
    default: m.DossierFilingListPage,
  })),
);
// Sprint 58.4.0 (Phase 25 / Sprint E1a) — PDPL Evidence Pack provider-config wizard.
const EmailProviderListPage = lazy(() =>
  import('./pages/admin/pdpl-evidence-packs/EmailProviderListPage.js').then((m) => ({
    default: m.EmailProviderListPage,
  })),
);
// Sprint 58.4.1 (Phase 25 / Sprint E1b) — PDPL Pack Builder + Email Draft + Pack Detail.
const PdplPackBuilderPage = lazy(() =>
  import('./pages/admin/pdpl-evidence-packs/PackBuilderPage.js').then((m) => ({
    default: m.PackBuilderPage,
  })),
);
const PdplPackDetailPage = lazy(() =>
  import('./pages/admin/pdpl-evidence-packs/PackDetailPage.js').then((m) => ({
    default: m.PackDetailPage,
  })),
);
const PdplEmailDraftPage = lazy(() =>
  import('./pages/admin/pdpl-evidence-packs/EmailDraftPage.js').then((m) => ({
    default: m.EmailDraftPage,
  })),
);
const DossierFilingDetailPage = lazy(() =>
  import('./pages/admin/assessments/DossierFilingDetailPage.js').then((m) => ({
    default: m.DossierFilingDetailPage,
  })),
);
const DossierMaterialisationHealthPage = lazy(() =>
  import('./pages/admin/assessments/DossierMaterialisationHealthPage.js').then((m) => ({
    default: m.DossierMaterialisationHealthPage,
  })),
);
const GapReportPage = lazy(() =>
  import('./pages/admin/assessments/GapReportPage.js').then((m) => ({ default: m.GapReportPage })),
);
// Sprint 62.35.0 (Slice C) — pre-collection protection assessments
const PreCollectionAssessmentsPage = lazy(() =>
  import('./pages/admin/assessments/PreCollectionAssessmentsPage.js').then((m) => ({
    default: m.PreCollectionAssessmentsPage,
  })),
);
// Sprint 1.6.0 Classification pages
const TaxonomiesPage = lazy(() =>
  import('./pages/admin/classification/TaxonomiesPage.js').then((m) => ({
    default: m.TaxonomiesPage,
  })),
);
const LabelingPage = lazy(() =>
  import('./pages/admin/classification/LabelingPage.js').then((m) => ({ default: m.LabelingPage })),
);
const LifecyclePage = lazy(() =>
  import('./pages/admin/classification/LifecyclePage.js').then((m) => ({
    default: m.LifecyclePage,
  })),
);
const ClassificationGapReportPage = lazy(() =>
  import('./pages/admin/classification/GapReportPage.js').then((m) => ({
    default: m.GapReportPage,
  })),
);
// Sprint 20.8.0 Classification Maturity pages
const ReviewTasksPage = lazy(() =>
  import('./pages/admin/classification/ReviewTasksPage.js').then((m) => ({
    default: m.ReviewTasksPage,
  })),
);
const ExceptionQueuePage = lazy(() =>
  import('./pages/admin/classification/ExceptionQueuePage.js').then((m) => ({
    default: m.ExceptionQueuePage,
  })),
);
const EnterpriseDataMapPage = lazy(() =>
  import('./pages/admin/classification/EnterpriseDataMapPage.js').then((m) => ({
    default: m.EnterpriseDataMapPage,
  })),
);
const ClassificationExportsPage = lazy(() =>
  import('./pages/admin/classification/ClassificationExportsPage.js').then((m) => ({
    default: m.ClassificationExportsPage,
  })),
);
const DiscoveryCyclesPage = lazy(() =>
  import('./pages/admin/classification/DiscoveryCyclesPage.js').then((m) => ({
    default: m.DiscoveryCyclesPage,
  })),
);
// Sprint 1.7.0 Monitoring pages
const ProgramsPage = lazy(() =>
  import('./pages/admin/monitoring/ProgramsPage.js').then((m) => ({ default: m.ProgramsPage })),
);
const PeriodsPage = lazy(() =>
  import('./pages/admin/monitoring/PeriodsPage.js').then((m) => ({ default: m.PeriodsPage })),
);
const TaskTemplatesPage = lazy(() =>
  import('./pages/admin/monitoring/TaskTemplatesPage.js').then((m) => ({
    default: m.TaskTemplatesPage,
  })),
);
const TasksPage = lazy(() =>
  import('./pages/admin/monitoring/TasksPage.js').then((m) => ({ default: m.TasksPage })),
);
const KpiDashboardPage = lazy(() =>
  import('./pages/admin/monitoring/KpiDashboardPage.js').then((m) => ({
    default: m.KpiDashboardPage,
  })),
);
const RicCasesPage = lazy(() =>
  import('./pages/admin/monitoring/RicCasesPage.js').then((m) => ({ default: m.RicCasesPage })),
);
// Sprint 21.3.0 Monitoring Maturity pages
const FrameworkLinkPage = lazy(() =>
  import('./pages/admin/monitoring/FrameworkLinkPage.js').then((m) => ({
    default: m.FrameworkLinkPage,
  })),
);
const AuthorityReadinessPage = lazy(() =>
  import('./pages/admin/monitoring/AuthorityReadinessPage.js').then((m) => ({
    default: m.AuthorityReadinessPage,
  })),
);
const ChangeReviewPage = lazy(() =>
  import('./pages/admin/monitoring/ChangeReviewPage.js').then((m) => ({
    default: m.ChangeReviewPage,
  })),
);
const GovernanceRaciPage = lazy(() =>
  import('./pages/admin/monitoring/GovernanceRaciPage.js').then((m) => ({
    default: m.GovernanceRaciPage,
  })),
);
// Sprint 1.8.0 Incident pages
const IncidentListPage = lazy(() =>
  import('./pages/admin/incidents/IncidentListPage.js').then((m) => ({
    default: m.IncidentListPage,
  })),
);
const IncidentDetailPage = lazy(() =>
  import('./pages/admin/incidents/IncidentDetailPage.js').then((m) => ({
    default: m.IncidentDetailPage,
  })),
);
const IncidentTasksPage = lazy(() =>
  import('./pages/admin/incidents/IncidentTasksPage.js').then((m) => ({
    default: m.IncidentTasksPage,
  })),
);
const IncidentReportsPage = lazy(() =>
  import('./pages/admin/incidents/IncidentReportsPage.js').then((m) => ({
    default: m.IncidentReportsPage,
  })),
);
// Sprint 21.1.0 Incident Response Maturity pages
const BreachNotificationsPage = lazy(() =>
  import('./pages/admin/incidents/BreachNotificationsPage.js').then((m) => ({
    default: m.BreachNotificationsPage,
  })),
);
const SupplementsPage = lazy(() =>
  import('./pages/admin/incidents/SupplementsPage.js').then((m) => ({
    default: m.SupplementsPage,
  })),
);
const RemediationPage = lazy(() =>
  import('./pages/admin/incidents/RemediationPage.js').then((m) => ({
    default: m.RemediationPage,
  })),
);
const AuthorityRequestsPage = lazy(() =>
  import('./pages/admin/incidents/AuthorityRequestsPage.js').then((m) => ({
    default: m.AuthorityRequestsPage,
  })),
);
const DisclosureRegisterPage = lazy(() =>
  import('./pages/admin/incidents/DisclosureRegisterPage.js').then((m) => ({
    default: m.DisclosureRegisterPage,
  })),
);
// Sprint 2.0.0 Connectors pages
const ConnectorDefinitionsPage = lazy(() =>
  import('./pages/admin/connectors/ConnectorDefinitionsPage.js').then((m) => ({
    default: m.ConnectorDefinitionsPage,
  })),
);
const ConnectorsPage = lazy(() =>
  import('./pages/admin/connectors/ConnectorsPage.js').then((m) => ({ default: m.ConnectorsPage })),
);
const CredentialsPage = lazy(() =>
  import('./pages/admin/connectors/CredentialsPage.js').then((m) => ({
    default: m.CredentialsPage,
  })),
);
const EventBusPage = lazy(() =>
  import('./pages/admin/connectors/EventBusPage.js').then((m) => ({ default: m.EventBusPage })),
);
// Sprint 21.6.0 Connectors maturity pages
const BrdCapturePage = lazy(() =>
  import('./pages/admin/connectors/BrdCapturePage.js').then((m) => ({
    default: m.BrdCapturePage,
  })),
);
const SecurityReviewsPage = lazy(() =>
  import('./pages/admin/connectors/SecurityReviewsPage.js').then((m) => ({
    default: m.SecurityReviewsPage,
  })),
);
const PocScorecardsPage = lazy(() =>
  import('./pages/admin/connectors/PocScorecardsPage.js').then((m) => ({
    default: m.PocScorecardsPage,
  })),
);
// Sprint 28.6.1 Connectors extended pages
const AutomationPlanningPage = lazy(() =>
  import('./pages/admin/connectors/AutomationPlanningPage.js').then((m) => ({
    default: m.AutomationPlanningPage,
  })),
);
const SystemInventoryPage = lazy(() =>
  import('./pages/admin/connectors/SystemInventoryPage.js').then((m) => ({
    default: m.SystemInventoryPage,
  })),
);
const TraceabilityMatrixPage = lazy(() =>
  import('./pages/admin/connectors/TraceabilityMatrixPage.js').then((m) => ({
    default: m.TraceabilityMatrixPage,
  })),
);
const GapRegisterPage = lazy(() =>
  import('./pages/admin/connectors/GapRegisterPage.js').then((m) => ({
    default: m.GapRegisterPage,
  })),
);
// Sprint 2.1.0 Vendor Risk pages
const VendorRiskDashboardPage = lazy(() =>
  import('./pages/admin/vendor-risk/VendorRiskDashboardPage.js').then((m) => ({
    default: m.VendorRiskDashboardPage,
  })),
);
const RiskScoringPage = lazy(() =>
  import('./pages/admin/vendor-risk/RiskScoringPage.js').then((m) => ({
    default: m.RiskScoringPage,
  })),
);
const VRQuestionnairesPage = lazy(() =>
  import('./pages/admin/vendor-risk/QuestionnairesPage.js').then((m) => ({
    default: m.QuestionnairesPage,
  })),
);
// Sprint 43.6.0 W2 — template structural editor + preview
const VRQuestionnaireTemplateEditor = lazy(() =>
  import('./pages/admin/vendor-risk/QuestionnaireTemplateEditor.js').then((m) => ({
    default: m.QuestionnaireTemplateEditor,
  })),
);
const VRQuestionnaireTemplatePreview = lazy(() =>
  import('./pages/admin/vendor-risk/QuestionnaireTemplatePreview.js').then((m) => ({
    default: m.QuestionnaireTemplatePreview,
  })),
);
// Sprint 47.5.8.1 — Contracts page lives in its own first-class module folder.
const ContractsPage = lazy(() =>
  import('./pages/admin/contracts/ContractsPage.js').then((m) => ({ default: m.ContractsPage })),
);
// Sprint 48.2.3 — Clause library admin page (consumed by redline assistant).
const ClauseLibraryPage = lazy(() =>
  import('./pages/admin/contracts/ClauseLibraryPage.js').then((m) => ({
    default: m.ClauseLibraryPage,
  })),
);
const GateEvaluationsPage = lazy(() =>
  import('./pages/admin/vendor-risk/GateEvaluationsPage.js').then((m) => ({
    default: m.GateEvaluationsPage,
  })),
);
const VendorReviewsPage = lazy(() =>
  import('./pages/admin/vendor-risk/VendorReviewsPage.js').then((m) => ({
    default: m.VendorReviewsPage,
  })),
);
// Sprint 21.0.0 Vendor Risk Maturity pages
const RiskSignalsPage = lazy(() =>
  import('./pages/admin/vendor-risk/RiskSignalsPage.js').then((m) => ({
    default: m.RiskSignalsPage,
  })),
);
const ReassessmentsPage = lazy(() =>
  import('./pages/admin/vendor-risk/ReassessmentsPage.js').then((m) => ({
    default: m.ReassessmentsPage,
  })),
);
const RiskPortfolioPage = lazy(() =>
  import('./pages/admin/vendor-risk/RiskPortfolioPage.js').then((m) => ({
    default: m.RiskPortfolioPage,
  })),
);
const RiskAcceptancePage = lazy(() =>
  import('./pages/admin/vendor-risk/RiskAcceptancePage.js').then((m) => ({
    default: m.RiskAcceptancePage,
  })),
);
const ReviewRoutingPage = lazy(() =>
  import('./pages/admin/vendor-risk/ReviewRoutingPage.js').then((m) => ({
    default: m.ReviewRoutingPage,
  })),
);
// Sprint 28.3.1 Vendor Onboarding Lifecycle pages
const VendorOnboardingCasePage = lazy(() =>
  import('./pages/admin/vendor-risk/VendorOnboardingCasePage.js').then((m) => ({
    default: m.VendorOnboardingCasePage,
  })),
);
const VendorSubProcessorRegisterPage = lazy(() =>
  import('./pages/admin/vendor-risk/VendorSubProcessorRegisterPage.js').then((m) => ({
    default: m.VendorSubProcessorRegisterPage,
  })),
);
// Sprint 2.2.0 Deletion Orchestration pages
const OrchestrationDashboardPage = lazy(() =>
  import('./pages/admin/deletion-orchestration/OrchestrationDashboardPage.js').then((m) => ({
    default: m.OrchestrationDashboardPage,
  })),
);
const OrchestrationRunsPage = lazy(() =>
  import('./pages/admin/deletion-orchestration/OrchestrationRunsPage.js').then((m) => ({
    default: m.OrchestrationRunsPage,
  })),
);
const ExecutionTasksPage = lazy(() =>
  import('./pages/admin/deletion-orchestration/ExecutionTasksPage.js').then((m) => ({
    default: m.ExecutionTasksPage,
  })),
);
const ChecklistTemplatesPage = lazy(() =>
  import('./pages/admin/deletion-orchestration/ChecklistTemplatesPage.js').then((m) => ({
    default: m.ChecklistTemplatesPage,
  })),
);
const VendorDeletionRequestsPage = lazy(() =>
  import('./pages/admin/deletion-orchestration/VendorDeletionRequestsPage.js').then((m) => ({
    default: m.VendorDeletionRequestsPage,
  })),
);
const ProofChainPage = lazy(() =>
  import('./pages/admin/deletion-orchestration/ProofChainPage.js').then((m) => ({
    default: m.ProofChainPage,
  })),
);
// Sprint 21.5.0 Deletion Orchestration Maturity pages
const DsrIntegrationPage = lazy(() =>
  import('./pages/admin/deletion-orchestration/DsrIntegrationPage.js').then((m) => ({
    default: m.DsrIntegrationPage,
  })),
);
const VendorVerificationPage = lazy(() =>
  import('./pages/admin/deletion-orchestration/VendorVerificationPage.js').then((m) => ({
    default: m.VendorVerificationPage,
  })),
);
const DeletionCompletenessPage = lazy(() =>
  import('./pages/admin/deletion-orchestration/CompletenessPage.js').then((m) => ({
    default: m.CompletenessPage,
  })),
);
// Sprint 2.3.0 Discovery & Classification pages
const DiscoveryDashboardPage = lazy(() =>
  import('./pages/admin/discovery/DiscoveryDashboardPage.js').then((m) => ({
    default: m.DiscoveryDashboardPage,
  })),
);
const DiscoveryScansPage = lazy(() =>
  import('./pages/admin/discovery/DiscoveryScansPage.js').then((m) => ({
    default: m.DiscoveryScansPage,
  })),
);
const StagingAreaPage = lazy(() =>
  import('./pages/admin/discovery/StagingAreaPage.js').then((m) => ({
    default: m.StagingAreaPage,
  })),
);
const DedupRulesPage = lazy(() =>
  import('./pages/admin/discovery/DedupRulesPage.js').then((m) => ({ default: m.DedupRulesPage })),
);
const ClassificationRulesPage = lazy(() =>
  import('./pages/admin/discovery/ClassificationRulesPage.js').then((m) => ({
    default: m.ClassificationRulesPage,
  })),
);
const SuggestionQueuePage = lazy(() =>
  import('./pages/admin/discovery/SuggestionQueuePage.js').then((m) => ({
    default: m.SuggestionQueuePage,
  })),
);
const SuggestionAnalyticsPage = lazy(() =>
  import('./pages/admin/discovery/SuggestionAnalyticsPage.js').then((m) => ({
    default: m.SuggestionAnalyticsPage,
  })),
);
const ImportTemplatesPage = lazy(() =>
  import('./pages/admin/discovery/ImportTemplatesPage.js').then((m) => ({
    default: m.ImportTemplatesPage,
  })),
);
const ConnectorManagementPage = lazy(() =>
  import('./pages/admin/discovery/ConnectorManagementPage.js').then((m) => ({
    default: m.ConnectorManagementPage,
  })),
);
// Sprint 21.4.0 Data Discovery Maturity pages
const DiscoveryCyclesMaturityPage = lazy(() =>
  import('./pages/admin/discovery/DiscoveryCyclesPage.js').then((m) => ({
    default: m.DiscoveryCyclesPage,
  })),
);
const OwnerReviewPage = lazy(() =>
  import('./pages/admin/discovery/OwnerReviewPage.js').then((m) => ({
    default: m.OwnerReviewPage,
  })),
);
const ClassificationLineagePage = lazy(() =>
  import('./pages/admin/discovery/ClassificationLineagePage.js').then((m) => ({
    default: m.ClassificationLineagePage,
  })),
);
// Sprint 2.4.0 Incident Operations Level 2 pages
const PlaybookTemplatesPage = lazy(() =>
  import('./pages/admin/incident-ops/PlaybookTemplatesPage.js').then((m) => ({
    default: m.PlaybookTemplatesPage,
  })),
);
const PlaybookActivationsPage = lazy(() =>
  import('./pages/admin/incident-ops/PlaybookActivationsPage.js').then((m) => ({
    default: m.PlaybookActivationsPage,
  })),
);
const SlaDefinitionsPage = lazy(() =>
  import('./pages/admin/incident-ops/SlaDefinitionsPage.js').then((m) => ({
    default: m.SlaDefinitionsPage,
  })),
);
const SlaDashboardPage = lazy(() =>
  import('./pages/admin/incident-ops/SlaDashboardPage.js').then((m) => ({
    default: m.SlaDashboardPage,
  })),
);
const EscalationLogPage = lazy(() =>
  import('./pages/admin/incident-ops/EscalationLogPage.js').then((m) => ({
    default: m.EscalationLogPage,
  })),
);
const RegulatorExportsPage = lazy(() =>
  import('./pages/admin/incident-ops/RegulatorExportsPage.js').then((m) => ({
    default: m.RegulatorExportsPage,
  })),
);
const VendorCoordinationPage = lazy(() =>
  import('./pages/admin/incident-ops/VendorCoordinationPage.js').then((m) => ({
    default: m.VendorCoordinationPage,
  })),
);
const ResponseMetricsPage = lazy(() =>
  import('./pages/admin/incident-ops/ResponseMetricsPage.js').then((m) => ({
    default: m.ResponseMetricsPage,
  })),
);
// Sprint 21.2.0 Incident Ops Maturity pages
const PlaybookLinksPage = lazy(() =>
  import('./pages/admin/incident-ops/PlaybookLinksPage.js').then((m) => ({
    default: m.PlaybookLinksPage,
  })),
);
const RegulatorySlaPage = lazy(() =>
  import('./pages/admin/incident-ops/RegulatorySlaPage.js').then((m) => ({
    default: m.RegulatorySlaPage,
  })),
);
const CoordinationDashboardPage = lazy(() =>
  import('./pages/admin/incident-ops/CoordinationDashboardPage.js').then((m) => ({
    default: m.CoordinationDashboardPage,
  })),
);
const EvidenceChainPage = lazy(() =>
  import('./pages/admin/incident-ops/EvidenceChainPage.js').then((m) => ({
    default: m.EvidenceChainPage,
  })),
);
const PostIncidentReviewPage = lazy(() =>
  import('./pages/admin/incident-ops/PostIncidentReviewPage.js').then((m) => ({
    default: m.PostIncidentReviewPage,
  })),
);
// Sprint 2.5.0 Program Governance pages
const MaturityModelsPage = lazy(() =>
  import('./pages/admin/program-governance/MaturityModelsPage.js').then((m) => ({
    default: m.MaturityModelsPage,
  })),
);
const MaturityAssessmentsPage = lazy(() =>
  import('./pages/admin/program-governance/MaturityAssessmentsPage.js').then((m) => ({
    default: m.MaturityAssessmentsPage,
  })),
);
// Sprint 64.165.0 — the official handbook evidence checklist, extracted from
// MaturityAssessmentsPage into its own surface.
const HandbookAssessmentsPage = lazy(() =>
  import('./pages/admin/program-governance/HandbookAssessmentsPage.js').then((m) => ({
    default: m.HandbookAssessmentsPage,
  })),
);
const TrendDashboardPage = lazy(() =>
  import('./pages/admin/program-governance/TrendDashboardPage.js').then((m) => ({
    default: m.TrendDashboardPage,
  })),
);
const ReportingPeriodsPage = lazy(() =>
  import('./pages/admin/program-governance/ReportingPeriodsPage.js').then((m) => ({
    default: m.ReportingPeriodsPage,
  })),
);
const ExecutiveSummariesPage = lazy(() =>
  import('./pages/admin/program-governance/ExecutiveSummariesPage.js').then((m) => ({
    default: m.ExecutiveSummariesPage,
  })),
);
const AuditReadinessPage = lazy(() =>
  import('./pages/admin/program-governance/AuditReadinessPage.js').then((m) => ({
    default: m.AuditReadinessPage,
  })),
);
const EvidenceGapsPage = lazy(() =>
  import('./pages/admin/program-governance/EvidenceGapsPage.js').then((m) => ({
    default: m.EvidenceGapsPage,
  })),
);
// Sprint 65.8.0 — the audit engagement register and the findings inside it
const AuditEngagementsPage = lazy(() =>
  import('./pages/admin/program-governance/AuditEngagementsPage.js').then((m) => ({
    default: m.AuditEngagementsPage,
  })),
);
const ProgramHealthPage = lazy(() =>
  import('./pages/admin/program-governance/ProgramHealthPage.js').then((m) => ({
    default: m.ProgramHealthPage,
  })),
);
// Sprint 21.7.0 Program Governance Maturity pages
const ObligationMappingsPage = lazy(() =>
  import('./pages/admin/program-governance/ObligationMappingsPage.js').then((m) => ({
    default: m.ObligationMappingsPage,
  })),
);
const GovernanceDocumentsPage = lazy(() =>
  import('./pages/admin/program-governance/GovernanceDocumentsPage.js').then((m) => ({
    default: m.GovernanceDocumentsPage,
  })),
);
const RemediationPlansPage = lazy(() =>
  import('./pages/admin/program-governance/RemediationPlansPage.js').then((m) => ({
    default: m.RemediationPlansPage,
  })),
);
// Sprint 65.17.0 — ANON-01 de-identification runs.
const DeidentificationRunsPage = lazy(() =>
  import('./pages/admin/deidentification/DeidentificationRunsPage.js').then((m) => ({
    default: m.DeidentificationRunsPage,
  })),
);
// Sprint 65.18.0 — IRP-01 continuity exercises.
const ContinuityExercisesPage = lazy(() =>
  import('./pages/admin/continuity/ContinuityExercisesPage.js').then((m) => ({
    default: m.ContinuityExercisesPage,
  })),
);
// Sprint 27.5.1 Governance Model & Document Issuance (UC-VN-17) pages
const GovernanceModelPage = lazy(() =>
  import('./pages/admin/program-governance/GovernanceModelPage.js').then((m) => ({
    default: m.GovernanceModelPage,
  })),
);
const DocumentDraftingPage = lazy(() =>
  import('./pages/admin/program-governance/DocumentDraftingPage.js').then((m) => ({
    default: m.DocumentDraftingPage,
  })),
);
const DocumentIssuancePage = lazy(() =>
  import('./pages/admin/program-governance/DocumentIssuancePage.js').then((m) => ({
    default: m.DocumentIssuancePage,
  })),
);
const AcknowledgmentTrackingPage = lazy(() =>
  import('./pages/admin/program-governance/AcknowledgmentTrackingPage.js').then((m) => ({
    default: m.AcknowledgmentTrackingPage,
  })),
);
const ChangeLogPage = lazy(() =>
  import('./pages/admin/program-governance/ChangeLogPage.js').then((m) => ({
    default: m.ChangeLogPage,
  })),
);
const ReviewCadencePage = lazy(() =>
  import('./pages/admin/program-governance/ReviewCadencePage.js').then((m) => ({
    default: m.ReviewCadencePage,
  })),
);
// Sprint 2.6.0 Access Accountability pages
const SessionDashboardPage = lazy(() =>
  import('./pages/admin/access-accountability/SessionDashboardPage.js').then((m) => ({
    default: m.SessionDashboardPage,
  })),
);
const AccessLogPage = lazy(() =>
  import('./pages/admin/access-accountability/AccessLogPage.js').then((m) => ({
    default: m.AccessLogPage,
  })),
);
const JustificationReviewPage = lazy(() =>
  import('./pages/admin/access-accountability/JustificationReviewPage.js').then((m) => ({
    default: m.JustificationReviewPage,
  })),
);
const InvestigationWorkspacePage = lazy(() =>
  import('./pages/admin/access-accountability/InvestigationWorkspacePage.js').then((m) => ({
    default: m.InvestigationWorkspacePage,
  })),
);
const PatternAnalysisPage = lazy(() =>
  import('./pages/admin/access-accountability/PatternAnalysisPage.js').then((m) => ({
    default: m.PatternAnalysisPage,
  })),
);
const AnchoringStatusPage = lazy(() =>
  import('./pages/admin/access-accountability/AnchoringStatusPage.js').then((m) => ({
    default: m.AnchoringStatusPage,
  })),
);
const AccessConfigPage = lazy(() =>
  import('./pages/admin/access-accountability/ConfigurationPage.js').then((m) => ({
    default: m.ConfigurationPage,
  })),
);
// Sprint 21.8.0 Access Accountability Maturity pages
const AccessPoliciesPage = lazy(() =>
  import('./pages/admin/access-accountability/AccessPoliciesPage.js').then((m) => ({
    default: m.AccessPoliciesPage,
  })),
);
const ReviewCampaignsPage = lazy(() =>
  import('./pages/admin/access-accountability/ReviewCampaignsPage.js').then((m) => ({
    default: m.ReviewCampaignsPage,
  })),
);
const EvidencePackagesPage = lazy(() =>
  import('./pages/admin/access-accountability/EvidencePackagesPage.js').then((m) => ({
    default: m.EvidencePackagesPage,
  })),
);
const EvidencePackageDetailPage = lazy(() =>
  import('./pages/admin/access-accountability/EvidencePackageDetailPage.js').then((m) => ({
    default: m.EvidencePackageDetailPage,
  })),
);
// Sprint 2.7.0 Consent Properties pages
const PropertyRegistryPage = lazy(() =>
  import('./pages/admin/consent-properties/PropertyRegistryPage.js').then((m) => ({
    default: m.PropertyRegistryPage,
  })),
);
const PropertyGroupsPage = lazy(() =>
  import('./pages/admin/consent-properties/PropertyGroupsPage.js').then((m) => ({
    default: m.PropertyGroupsPage,
  })),
);
const BindingMatrixPage = lazy(() =>
  import('./pages/admin/consent-properties/BindingMatrixPage.js').then((m) => ({
    default: m.BindingMatrixPage,
  })),
);
const ChangeRequestPipelinePage = lazy(() =>
  import('./pages/admin/consent-properties/ChangeRequestPipelinePage.js').then((m) => ({
    default: m.ChangeRequestPipelinePage,
  })),
);
const ComparisonWorkspacePage = lazy(() =>
  import('./pages/admin/consent-properties/ComparisonWorkspacePage.js').then((m) => ({
    default: m.ComparisonWorkspacePage,
  })),
);
const InconsistencyListPage = lazy(() =>
  import('./pages/admin/consent-properties/InconsistencyListPage.js').then((m) => ({
    default: m.InconsistencyListPage,
  })),
);
const SnapshotSchedulesPage = lazy(() =>
  import('./pages/admin/consent-properties/SnapshotSchedulesPage.js').then((m) => ({
    default: m.SnapshotSchedulesPage,
  })),
);
const GoldenTemplatesPage = lazy(() =>
  import('./pages/admin/consent-properties/GoldenTemplatesPage.js').then((m) => ({
    default: m.GoldenTemplatesPage,
  })),
);
const EvidencePacksPage = lazy(() =>
  import('./pages/admin/consent-properties/EvidencePacksPage.js').then((m) => ({
    default: m.EvidencePacksPage,
  })),
);
const CrossPropertyDashboardPage = lazy(() =>
  import('./pages/admin/consent-properties/CrossPropertyDashboardPage.js').then((m) => ({
    default: m.CrossPropertyDashboardPage,
  })),
);
const ConsentPropertyAuditPage = lazy(() =>
  import('./pages/admin/consent-properties/ConsentPropertyAuditPage.js').then((m) => ({
    default: m.ConsentPropertyAuditPage,
  })),
);
// Sprint 20.7.0 Consent Properties Maturity pages
const TemplateRolloutsPage = lazy(() =>
  import('./pages/admin/consent-properties/TemplateRolloutsPage.js').then((m) => ({
    default: m.TemplateRolloutsPage,
  })),
);
const PropertyAnalyticsPage = lazy(() =>
  import('./pages/admin/consent-properties/PropertyAnalyticsPage.js').then((m) => ({
    default: m.PropertyAnalyticsPage,
  })),
);
// Sprint 2.8.0 Localization pages
const LocalizationDashboardPage = lazy(() =>
  import('./pages/admin/localization/LocalizationDashboardPage.js').then((m) => ({
    default: m.LocalizationDashboardPage,
  })),
);
const MarketPacksCatalogPage = lazy(() =>
  import('./pages/admin/localization/MarketPacksCatalogPage.js').then((m) => ({
    default: m.MarketPacksCatalogPage,
  })),
);
const PackDetailPage = lazy(() =>
  import('./pages/admin/localization/PackDetailPage.js').then((m) => ({
    default: m.PackDetailPage,
  })),
);
const LanguagePacksPage = lazy(() =>
  import('./pages/admin/localization/LanguagePacksPage.js').then((m) => ({
    default: m.LanguagePacksPage,
  })),
);
// Sprint 45.8.0 — Help-Content Pack admin
const HelpContentPacksPage = lazy(() =>
  import('./pages/admin/localization/HelpContentPacksPage.js').then((m) => ({
    default: m.HelpContentPacksPage,
  })),
);
// Sprint 46.2.0 — sector overlay catalog page (AC-6) + org sectors picker (AC-5 FE)
const SectorOverlayCatalogPage = lazy(() =>
  import('./pages/admin/localization/SectorOverlayCatalogPage.js').then((m) => ({
    default: m.SectorOverlayCatalogPage,
  })),
);
const OrgSectorsPage = lazy(() =>
  import('./pages/admin/settings/OrgSectorsPage.js').then((m) => ({ default: m.OrgSectorsPage })),
);
// Sprint 63.43.0 (AC4) — Module 30 Sector Overlays: the Overview page, first leaf of
// Sector Overlays › Overlay Management.
const SectorOverlaysOverviewPage = lazy(() =>
  import('./pages/admin/sector-overlays/SectorOverlaysOverviewPage.js').then((m) => ({
    default: m.SectorOverlaysOverviewPage,
  })),
);

// Sprint 65.21.0 — the credit-information use register.
const CreditInformationUsesPage = lazy(() =>
  import('./pages/admin/credit-information/CreditInformationUsesPage.js').then((m) => ({
    default: m.CreditInformationUsesPage,
  })),
);

// Sprint 65.19.0 — LEG-01 legal matters.
const LegalMattersPage = lazy(() =>
  import('./pages/admin/legal-matters/LegalMattersPage.js').then((m) => ({
    default: m.LegalMattersPage,
  })),
);

const RegulatoryFrameworksPage = lazy(() =>
  import('./pages/admin/localization/RegulatoryFrameworksPage.js').then((m) => ({
    default: m.RegulatoryFrameworksPage,
  })),
);
// Sprint 47.1.0 — SEF URL for framework detail (`/frameworks/:frameworkCode`).
const FrameworkDetailPage = lazy(() =>
  import('./pages/admin/localization/FrameworkDetailPage.js').then((m) => ({
    default: m.FrameworkDetailPage,
  })),
);
const ModuleMappingsPage = lazy(() =>
  import('./pages/admin/localization/ModuleMappingsPage.js').then((m) => ({
    default: m.ModuleMappingsPage,
  })),
);
const PackSubmissionsPage = lazy(() =>
  import('./pages/admin/localization/PackSubmissionsPage.js').then((m) => ({
    default: m.PackSubmissionsPage,
  })),
);
const TranslationPreviewPage = lazy(() =>
  import('./pages/admin/localization/TranslationPreviewPage.js').then((m) => ({
    default: m.TranslationPreviewPage,
  })),
);
const LocalizationAuditPage = lazy(() =>
  import('./pages/admin/localization/LocalizationAuditPage.js').then((m) => ({
    default: m.LocalizationAuditPage,
  })),
);
// Sprint 22.2.0: Localization Module Maturity
const FrameworkModuleLinksPage = lazy(() =>
  import('./pages/admin/localization/FrameworkModuleLinksPage.js').then((m) => ({
    default: m.FrameworkModuleLinksPage,
  })),
);
const FrameworkChannelsPage = lazy(() =>
  import('./pages/admin/localization/FrameworkChannelsPage.js').then((m) => ({
    default: m.FrameworkChannelsPage,
  })),
);
const RegulatoryUpdatesPage = lazy(() =>
  import('./pages/admin/localization/RegulatoryUpdatesPage.js').then((m) => ({
    default: m.RegulatoryUpdatesPage,
  })),
);
// Sprint 62.58.0 (Slice F, AC-F3) — one regulatory update's comparison; no sidebar leaf.
const RegulatoryUpdateComparisonPage = lazy(() =>
  import('./pages/admin/localization/RegulatoryUpdateComparisonPage.js').then((m) => ({
    default: m.RegulatoryUpdateComparisonPage,
  })),
);
// Sprint 2.9.0 Partner Marketplace pages
const PartnerDashboardPage = lazy(() =>
  import('./pages/admin/partner-marketplace/PartnerDashboardPage.js').then((m) => ({
    default: m.PartnerDashboardPage,
  })),
);
const PartnerOrgsPage = lazy(() =>
  import('./pages/admin/partner-marketplace/PartnerOrgsPage.js').then((m) => ({
    default: m.PartnerOrgsPage,
  })),
);
const ServiceTiersPage = lazy(() =>
  import('./pages/admin/partner-marketplace/ServiceTiersPage.js').then((m) => ({
    default: m.ServiceTiersPage,
  })),
);
const DeliverableTemplatesPage = lazy(() =>
  import('./pages/admin/partner-marketplace/DeliverableTemplatesPage.js').then((m) => ({
    default: m.DeliverableTemplatesPage,
  })),
);
const DeliverablesPage = lazy(() =>
  import('./pages/admin/partner-marketplace/DeliverablesPage.js').then((m) => ({
    default: m.DeliverablesPage,
  })),
);
const BrandProfilesPage = lazy(() =>
  import('./pages/admin/partner-marketplace/BrandProfilesPage.js').then((m) => ({
    default: m.BrandProfilesPage,
  })),
);
const SupportTicketsPage = lazy(() =>
  import('./pages/admin/partner-marketplace/SupportTicketsPage.js').then((m) => ({
    default: m.SupportTicketsPage,
  })),
);
const PartnerTicketDetailPage = lazy(() =>
  import('./pages/admin/partner-marketplace/TicketDetailPage.js').then((m) => ({
    default: m.TicketDetailPage,
  })),
);
const WorkflowTemplatesPage = lazy(() =>
  import('./pages/admin/partner-marketplace/WorkflowTemplatesPage.js').then((m) => ({
    default: m.WorkflowTemplatesPage,
  })),
);
const WorkflowInstancesPage = lazy(() =>
  import('./pages/admin/partner-marketplace/WorkflowInstancesPage.js').then((m) => ({
    default: m.WorkflowInstancesPage,
  })),
);
const EscalationDashboardPage = lazy(() =>
  import('./pages/admin/partner-marketplace/EscalationDashboardPage.js').then((m) => ({
    default: m.EscalationDashboardPage,
  })),
);
const PartnerAuditPage = lazy(() =>
  import('./pages/admin/partner-marketplace/PartnerAuditPage.js').then((m) => ({
    default: m.PartnerAuditPage,
  })),
);
const PartnerOrgDetailPage = lazy(() =>
  import('./pages/admin/partner-marketplace/PartnerOrgDetailPage.js').then((m) => ({
    default: m.PartnerOrgDetailPage,
  })),
);
const PartnerReportsPage = lazy(() =>
  import('./pages/admin/partner-marketplace/PartnerReportsPage.js').then((m) => ({
    default: m.PartnerReportsPage,
  })),
);
// Sprint 1.9.0 Onboarding pages
const SetupWizardPage = lazy(() =>
  import('./pages/admin/onboarding/SetupWizardPage.js').then((m) => ({
    default: m.SetupWizardPage,
  })),
);
const SampleDataPage = lazy(() =>
  import('./pages/admin/onboarding/SampleDataPage.js').then((m) => ({ default: m.SampleDataPage })),
);
const ToursPage = lazy(() =>
  import('./pages/admin/onboarding/ToursPage.js').then((m) => ({ default: m.ToursPage })),
);
const HelpCenterPage = lazy(() =>
  import('./pages/admin/onboarding/HelpCenterPage.js').then((m) => ({ default: m.HelpCenterPage })),
);
// Sprint 44.1.0 — FAQ editor scaffolding (permission-gated stub page)
const HelpFaqEditorPage = lazy(() =>
  import('./pages/admin/onboarding/HelpFaqEditorPage.js').then((m) => ({
    default: m.HelpFaqEditorPage,
  })),
);
// Sprint 22.4.0 Onboarding Maturity pages
const QuickStartsPage = lazy(() =>
  import('./pages/admin/onboarding/QuickStartsPage.js').then((m) => ({
    default: m.QuickStartsPage,
  })),
);
const OnboardingProgressPage = lazy(() =>
  import('./pages/admin/onboarding/OnboardingProgressPage.js').then((m) => ({
    default: m.OnboardingProgressPage,
  })),
);
const PersonaOnboardingPage = lazy(() =>
  import('./pages/admin/onboarding/PersonaOnboardingPage.js').then((m) => ({
    default: m.PersonaOnboardingPage,
  })),
);
// Sprint 63.11.0 (#34) — the destinations of the {{persona:…}} / {{usecase:…}}
// help-content tokens, read from the shared persona and use-case registries.
const PersonaDetailPage = lazy(() =>
  import('./pages/admin/onboarding/PersonaDetailPage.js').then((m) => ({
    default: m.PersonaDetailPage,
  })),
);
const UseCaseDetailPage = lazy(() =>
  import('./pages/admin/onboarding/UseCaseDetailPage.js').then((m) => ({
    default: m.UseCaseDetailPage,
  })),
);
// Sprint 3.6.0 Settings pages
const WebhooksPage = lazy(() =>
  import('./pages/admin/settings/WebhooksPage.js').then((m) => ({ default: m.WebhooksPage })),
);
const DomainsPage = lazy(() =>
  import('./pages/admin/settings/DomainsPage.js').then((m) => ({ default: m.DomainsPage })),
);
const ExportSchedulesPage = lazy(() =>
  import('./pages/admin/settings/ExportSchedulesPage.js').then((m) => ({
    default: m.ExportSchedulesPage,
  })),
);
const EmailSettingsPage = lazy(() =>
  import('./pages/admin/settings/EmailSettingsPage.js').then((m) => ({
    default: m.EmailSettingsPage,
  })),
);
// Sprint 40.1.0 Assessment Triggers settings
const AssessmentTriggersPage = lazy(() =>
  import('./pages/admin/settings/AssessmentTriggersPage.js').then((m) => ({
    default: m.AssessmentTriggersPage,
  })),
);
// Sprint 4.1.0 Privacy Scanner pages
const MonitoringDashboardPage = lazy(() =>
  import('./pages/admin/privacy-scanner/MonitoringDashboardPage.js').then((m) => ({
    default: m.MonitoringDashboardPage,
  })),
);
const MonitoringSettingsPage = lazy(() =>
  import('./pages/admin/privacy-scanner/MonitoringSettingsPage.js').then((m) => ({
    default: m.MonitoringSettingsPage,
  })),
);
const RealTimeScanPage = lazy(() =>
  import('./pages/admin/privacy-scanner/RealTimeScanPage.js').then((m) => ({
    default: m.RealTimeScanPage,
  })),
);
const ScannerSettingsPage = lazy(() =>
  import('./pages/admin/privacy-scanner/ScannerSettingsPage.js').then((m) => ({
    default: m.ScannerSettingsPage,
  })),
);
const ScanResultsArchivePage = lazy(() =>
  import('./pages/admin/privacy-scanner/ScanResultsArchivePage.js').then((m) => ({
    default: m.ScanResultsArchivePage,
  })),
);
const ScanResultDetailPage = lazy(() =>
  import('./pages/admin/privacy-scanner/ScanResultDetailPage.js').then((m) => ({
    default: m.ScanResultDetailPage,
  })),
);
const DashboardPage = lazy(() =>
  import('./pages/admin/DashboardPage.js').then((m) => ({ default: m.DashboardPage })),
);
// Sprint 4.4.0 Module Configuration
const ModuleConfigPage = lazy(() =>
  import('./pages/admin/platform/ModuleConfigPage.js').then((m) => ({
    default: m.ModuleConfigPage,
  })),
);
// Sprint 18.0.0 License & Package Management
// Sprint 63.37.0 (E0) — Settings › Organisation identity.
const OrgIdentityPage = lazy(() =>
  import('./pages/admin/settings/OrgIdentityPage.js').then((m) => ({
    default: m.OrgIdentityPage,
  })),
);
const LicenseSettingsPage = lazy(() =>
  import('./pages/admin/platform/LicenseSettingsPage.js').then((m) => ({
    default: m.LicenseSettingsPage,
  })),
);
// Sprint 41.4.0 — Platform → Files Configuration & Browser
const FilesConfigPage = lazy(() =>
  import('./pages/admin/platform/FilesConfigPage.js').then((m) => ({
    default: m.FilesConfigPage,
  })),
);
const FilesBrowserPage = lazy(() =>
  import('./pages/admin/platform/FilesBrowserPage.js').then((m) => ({
    default: m.FilesBrowserPage,
  })),
);
const FilesDashboardPage = lazy(() =>
  import('./pages/admin/platform/FilesDashboardPage.js').then((m) => ({
    default: m.FilesDashboardPage,
  })),
);
// Owner ruling (2026-10-10) — supersedes Sprint 47.8.0e. 47.8.0e retired the
// `/admin/settings/forseti` provider panel because AesirX was the supplier and
// customers bought prepaid Forseti tokens without seeing the provider. That is
// no longer the model: each organisation configures its own AI provider
// (Anthropic, OpenAI or an OpenAI-compatible endpoint), key, model and effort
// at `/admin/forseti/ai-model`, and pays that provider directly. The prepaid
// bundles are gone, and the Usage page at `/admin/forseti/usage` reports
// consumption with the provider, model and effort each request ran on.
// The old `/admin/settings/forseti` route stays retired.
const ForsetiAiModelSettingsPage = lazy(() =>
  import('./pages/admin/forseti/ai-model/AiModelSettingsPage.js').then((m) => ({
    default: m.AiModelSettingsPage,
  })),
);
// Sprint 47.5.8 — Advisor audit lineage page (cross-module Forseti graph)
const AdvisorLineagePage = lazy(() =>
  import('./pages/admin/forseti/audit-lineage/AdvisorLineagePage.js').then((m) => ({
    default: m.AdvisorLineagePage,
  })),
);
// Sprint 47.5.8.6 — AI evidence review page (move-to-review / approve / reject).
const DraftEvidencePage = lazy(() =>
  import('./pages/admin/forseti/draft-evidence/DraftEvidencePage.js').then((m) => ({
    default: m.DraftEvidencePage,
  })),
);
const ForsetiUsageDashboardPage = lazy(() =>
  import('./pages/admin/forseti/usage/UsageDashboardPage.js').then((m) => ({
    default: m.UsageDashboardPage,
  })),
);
// Sprint 47.5.9.6 — Aesir-internal reconciliation drift report (stub
// page; persisted-run backend deferred — see ReconciliationPage.tsx).
const ForsetiReconciliationPage = lazy(() =>
  import('./pages/admin/forseti/usage/ReconciliationPage.js').then((m) => ({
    default: m.ReconciliationPage,
  })),
);
// Sprint 47.9.7 — MCP connector admin.
const ForsetiMcpKeysPage = lazy(() =>
  import('./pages/admin/forseti/mcp-keys/McpKeysPage.js').then((m) => ({
    default: m.McpKeysPage,
  })),
);
// Sprint 48.0.0c — Forseti memory browse / curate.
const ForsetiMemoryBrowsePage = lazy(() =>
  import('./pages/admin/forseti/memory/MemoryBrowsePage.js').then((m) => ({
    default: m.MemoryBrowsePage,
  })),
);
// Sprint 48.2.8 — Compliance rule auto-mapping page.
const RuleMappingPage = lazy(() =>
  import('./pages/admin/compliance-rules/RuleMappingPage.js').then((m) => ({
    default: m.RuleMappingPage,
  })),
);
// Sprint 48.3.0 — MCP Phase 2 pending writes page.
const McpPendingWritesPage = lazy(() =>
  import('./pages/admin/forseti/mcp-pending/McpPendingWritesPage.js').then((m) => ({
    default: m.McpPendingWritesPage,
  })),
);
// Sprint 48.2.9 — Audit walkthrough + Privilege creep findings pages.
const AuditWalkthroughPage = lazy(() =>
  import('./pages/admin/audits/AuditWalkthroughPage.js').then((m) => ({
    default: m.AuditWalkthroughPage,
  })),
);
const PrivilegeCreepFindingsPage = lazy(() =>
  import('./pages/admin/access-accountability/PrivilegeCreepFindingsPage.js').then((m) => ({
    default: m.PrivilegeCreepFindingsPage,
  })),
);
// Sprint 47.5.2 — Forseti Projects (overview + detail).
const ForsetiProjectsOverviewPage = lazy(() =>
  import('./pages/admin/forseti/projects/ProjectsOverviewPage.js').then((m) => ({
    default: m.ProjectsOverviewPage,
  })),
);
const ForsetiProjectDetailPage = lazy(() =>
  import('./pages/admin/forseti/projects/ProjectDetailPage.js').then((m) => ({
    default: m.ProjectDetailPage,
  })),
);
// Sprint 47.5.3a — Forseti Document Review (versions + AI edits).
const ForsetiDocumentReviewPage = lazy(() =>
  import('./pages/admin/forseti/document-review/DocumentReviewPage.js').then((m) => ({
    default: m.DocumentReviewPage,
  })),
);
// Sprint 47.8.0a — Forseti Document Review index (replaces blank screen
// at `/admin/forseti/document-review`).
const ForsetiDocumentReviewIndexPage = lazy(() =>
  import('./pages/admin/forseti/document-review/DocumentReviewIndexPage.js').then((m) => ({
    default: m.DocumentReviewIndexPage,
  })),
);
// Sprint 47.5.4a — Forseti chat shell.
const ForsetiOverviewPage = lazy(() =>
  import('./pages/admin/forseti/chats/ForsetiOverviewPage.js').then((m) => ({
    default: m.ForsetiOverviewPage,
  })),
);
// Sprint 47.5.6 — Forseti Tabular Reviews.
const ForsetiTabularReviewsListPage = lazy(() =>
  import('./pages/admin/forseti/tabular-reviews/TabularReviewsListPage.js').then((m) => ({
    default: m.TabularReviewsListPage,
  })),
);
const ForsetiTabularReviewDetailPage = lazy(() =>
  import('./pages/admin/forseti/tabular-reviews/TabularReviewDetailPage.js').then((m) => ({
    default: m.TabularReviewDetailPage,
  })),
);
// Sprint 47.7.2 — cross-review comparison page.
const ForsetiTabularReviewsComparePage = lazy(() =>
  import('./pages/admin/forseti/tabular-reviews/TabularReviewsComparePage.js').then((m) => ({
    default: m.TabularReviewsComparePage,
  })),
);
// Sprint 47.7.3 — cross-framework conflicts page.
const ForsetiCrossFrameworkConflictsPage = lazy(() =>
  import('./pages/admin/forseti/cross-framework-conflicts/CrossFrameworkConflictsPage.js').then(
    (m) => ({
      default: m.CrossFrameworkConflictsPage,
    }),
  ),
);
// Sprint 47.5.5 — Forseti AI Workflows.
const ForsetiWorkflowListPage = lazy(() =>
  import('./pages/admin/forseti/workflows/WorkflowListPage.js').then((m) => ({
    default: m.WorkflowListPage,
  })),
);
const ForsetiWorkflowDetailPage = lazy(() =>
  import('./pages/admin/forseti/workflows/WorkflowDetailPage.js').then((m) => ({
    default: m.WorkflowDetailPage,
  })),
);
const ForsetiChatDetailPage = lazy(() =>
  import('./pages/admin/forseti/chats/ChatDetailPage.js').then((m) => ({
    default: m.ChatDetailPage,
  })),
);

// Sprint 47.4.0 — Platform → Immutable Audit Trails (foundation shells)
const IatSettingsPage = lazy(() =>
  import('./pages/admin/platform/immutable-audit-trails/SettingsPage.js').then((m) => ({
    default: m.SettingsPage,
  })),
);
const IatProofStatusPage = lazy(() =>
  import('./pages/admin/platform/immutable-audit-trails/ProofStatusPage.js').then((m) => ({
    default: m.ProofStatusPage,
  })),
);
const IatIntegrityCenterPage = lazy(() =>
  import('./pages/admin/platform/immutable-audit-trails/IntegrityCenterPage.js').then((m) => ({
    default: m.IntegrityCenterPage,
  })),
);
const IatUsagePage = lazy(() =>
  import('./pages/admin/platform/immutable-audit-trails/UsagePage.js').then((m) => ({
    default: m.UsagePage,
  })),
);
// Sprint 7.6.0 Form Engine pages
const FormsDashboardPage = lazy(() =>
  import('./pages/admin/forms/FormsDashboardPage.js').then((m) => ({
    default: m.FormsDashboardPage,
  })),
);
const TemplateBrowserPage = lazy(() =>
  import('./pages/admin/forms/TemplateBrowserPage.js').then((m) => ({
    default: m.TemplateBrowserPage,
  })),
);
const FormFillerPage = lazy(() =>
  import('./pages/admin/forms/FormFillerPage.js').then((m) => ({ default: m.FormFillerPage })),
);
const FormInstanceDetailPage = lazy(() =>
  import('./pages/admin/forms/FormInstanceDetailPage.js').then((m) => ({
    default: m.FormInstanceDetailPage,
  })),
);
const FormOutputsPage = lazy(() =>
  import('./pages/admin/forms/FormOutputsPage.js').then((m) => ({ default: m.FormOutputsPage })),
);
// Sprint 10.6.0 Form Catalog page
const FormCatalogPage = lazy(() =>
  import('./pages/admin/forms/FormCatalogPage.js').then((m) => ({
    default: m.FormCatalogPage,
  })),
);
// Sprint 21.9.0 Compliance Forms Maturity pages
const FilingCaseLinksPage = lazy(() =>
  import('./pages/admin/forms/FilingCaseLinksPage.js').then((m) => ({
    default: m.FilingCaseLinksPage,
  })),
);
const SectionRoutingPage = lazy(() =>
  import('./pages/admin/forms/SectionRoutingPage.js').then((m) => ({
    default: m.SectionRoutingPage,
  })),
);
const SubmissionPackagesPage = lazy(() =>
  import('./pages/admin/forms/SubmissionPackagesPage.js').then((m) => ({
    default: m.SubmissionPackagesPage,
  })),
);
// Sprint 11.5.0 Org Groups pages (Phase 9: Corporate Governance)
const OrgGroupsPage = lazy(() =>
  import('./pages/admin/org-groups/OrgGroupsPage.js').then((m) => ({
    default: m.OrgGroupsPage,
  })),
);
const OrgGroupDetailPage = lazy(() =>
  import('./pages/admin/org-groups/OrgGroupDetailPage.js').then((m) => ({
    default: m.OrgGroupDetailPage,
  })),
);
const OrgGroupSettingsPage = lazy(() =>
  import('./pages/admin/org-groups/OrgGroupSettingsPage.js').then((m) => ({
    default: m.OrgGroupSettingsPage,
  })),
);
const OrgGroupAuditPage = lazy(() =>
  import('./pages/admin/org-groups/OrgGroupAuditPage.js').then((m) => ({
    default: m.OrgGroupAuditPage,
  })),
);
// Sprint 65.6.0 (AC3) — DT-01. `/admin/training` is the route
// `TaskSourceLink`'s `MODULE_ROUTE_MAP` has named since long before any module
// could emit a `training` task source, and which this file never declared.
const TrainingPage = lazy(() =>
  import('./pages/admin/training/TrainingPage.js').then((m) => ({
    default: m.TrainingPage,
  })),
);
/*
 * Sprint 65.6.0 (AC8) — DPO-01 and PUB-01.
 *
 * `src/backend/src/modules/privacy-office/` shipped its records, services and
 * mounted routes with no page and zero references in this file, so an operator
 * could not record a DPO appointment or publish a privacy notice from the
 * product at all. Two pages rather than one: `routes.ts` declares
 * `dpo_appointment:*` and `privacy_notice:*` as two permission pairs because
 * they are separate registers with separate readers.
 */
const DpoAppointmentsPage = lazy(() =>
  import('./pages/admin/privacy-office/DpoAppointmentsPage.js').then((m) => ({
    default: m.DpoAppointmentsPage,
  })),
);
const PrivacyNoticesPage = lazy(() =>
  import('./pages/admin/privacy-office/PrivacyNoticesPage.js').then((m) => ({
    default: m.PrivacyNoticesPage,
  })),
);
// Sprint 62.57.0 Slice E (D67) — the processing-service certificate register.
const ServiceCertificateRegisterPage = lazy(() =>
  import('./pages/admin/service-certificates/ServiceCertificateRegisterPage.js').then((m) => ({
    default: m.ServiceCertificateRegisterPage,
  })),
);
const ServiceCertificateDetailPage = lazy(() =>
  import('./pages/admin/service-certificates/ServiceCertificateDetailPage.js').then((m) => ({
    default: m.ServiceCertificateDetailPage,
  })),
);
const ServiceCertificateApplicationDetailPage = lazy(() =>
  import('./pages/admin/service-certificates/ServiceCertificateApplicationDetailPage.js').then(
    (m) => ({ default: m.ServiceCertificateApplicationDetailPage }),
  ),
);
// Sprint 11.6.0 Shared Data pages (Phase 9: Corporate Governance)
const SharedVendorsPage = lazy(() =>
  import('./pages/admin/org-groups/SharedVendorsPage.js').then((m) => ({
    default: m.SharedVendorsPage,
  })),
);
const SharedFrameworksPage = lazy(() =>
  import('./pages/admin/org-groups/SharedFrameworksPage.js').then((m) => ({
    default: m.SharedFrameworksPage,
  })),
);
const SharedFormTemplatesPage = lazy(() =>
  import('./pages/admin/org-groups/SharedFormTemplatesPage.js').then((m) => ({
    default: m.SharedFormTemplatesPage,
  })),
);
const SettingsInheritancePage = lazy(() =>
  import('./pages/admin/org-groups/SettingsInheritancePage.js').then((m) => ({
    default: m.SettingsInheritancePage,
  })),
);
const MergedVendorsPage = lazy(() =>
  import('./pages/admin/org-groups/MergedVendorsPage.js').then((m) => ({
    default: m.MergedVendorsPage,
  })),
);
// Sprint 11.7.0 Cross-Org pages (Phase 9: Corporate Governance)
const GroupUsersPage = lazy(() =>
  import('./pages/admin/org-groups/GroupUsersPage.js').then((m) => ({
    default: m.GroupUsersPage,
  })),
);
const GroupAuditPage = lazy(() =>
  import('./pages/admin/org-groups/GroupAuditPage.js').then((m) => ({
    default: m.GroupAuditPage,
  })),
);

// Phase 9 Extension: Organization Management & Public Intake Config
const OrganizationsPage = lazy(() =>
  import('./pages/admin/organizations/OrganizationsPage.js').then((m) => ({
    default: m.OrganizationsPage,
  })),
);
const OrganizationFormPage = lazy(() =>
  import('./pages/admin/organizations/OrganizationFormPage.js').then((m) => ({
    default: m.OrganizationFormPage,
  })),
);
const IntakeConfigsPage = lazy(() =>
  import('./pages/admin/organizations/IntakeConfigsPage.js').then((m) => ({
    default: m.IntakeConfigsPage,
  })),
);
const IntakeConfigFormPage = lazy(() =>
  import('./pages/admin/organizations/IntakeConfigFormPage.js').then((m) => ({
    default: m.IntakeConfigFormPage,
  })),
);

// Sprint 22.3.0 Organizations Module Maturity
const DepartmentsPage = lazy(() =>
  import('./pages/admin/organizations/DepartmentsPage.js').then((m) => ({
    default: m.DepartmentsPage,
  })),
);
// Sprint 43.2.0 batch 3 — admin pages for Response Templates / Departments / Retention
const ResponseTemplatesAdminPage = lazy(() =>
  import('./pages/admin/rights-requests/ResponseTemplatesAdminPage.js').then((m) => ({
    default: m.ResponseTemplatesAdminPage,
  })),
);
const DepartmentsAdminPage = lazy(() =>
  import('./pages/admin/organizations/DepartmentsAdminPage.js').then((m) => ({
    default: m.DepartmentsAdminPage,
  })),
);
const RetentionPoliciesAdminPage = lazy(() =>
  import('./pages/admin/organizations/RetentionPoliciesAdminPage.js').then((m) => ({
    default: m.RetentionPoliciesAdminPage,
  })),
);
const EntityFrameworkAssignmentsPage = lazy(() =>
  import('./pages/admin/organizations/EntityFrameworkAssignmentsPage.js').then((m) => ({
    default: m.EntityFrameworkAssignmentsPage,
  })),
);
const ComplianceRollupPage = lazy(() =>
  import('./pages/admin/organizations/ComplianceRollupPage.js').then((m) => ({
    default: m.ComplianceRollupPage,
  })),
);

// Sprint 17.4.0 Task Management pages
const TaskCalendarPage = lazy(() =>
  import('./pages/admin/tasks/CalendarPage.js').then((m) => ({ default: m.CalendarPage })),
);
const TaskListPage = lazy(() =>
  import('./pages/admin/tasks/TaskListPage.js').then((m) => ({ default: m.TaskListPage })),
);
const TaskDetailPage = lazy(() =>
  import('./pages/admin/tasks/TaskDetailPage.js').then((m) => ({ default: m.TaskDetailPage })),
);
const MyTasksPage = lazy(() =>
  import('./pages/admin/tasks/MyTasksPage.js').then((m) => ({ default: m.MyTasksPage })),
);
const OverdueTasksPage = lazy(() =>
  import('./pages/admin/tasks/OverdueTasksPage.js').then((m) => ({ default: m.OverdueTasksPage })),
);
const TaskBoardPage = lazy(() =>
  import('./pages/admin/tasks/TaskBoardPage.js').then((m) => ({ default: m.TaskBoardPage })),
);
const WorkflowListPage = lazy(() =>
  import('./pages/admin/tasks/WorkflowListPage.js').then((m) => ({ default: m.WorkflowListPage })),
);
const WorkflowDetailPage = lazy(() =>
  import('./pages/admin/tasks/WorkflowDetailPage.js').then((m) => ({
    default: m.WorkflowDetailPage,
  })),
);
const WorkflowTemplateCatalog = lazy(() =>
  import('./pages/admin/tasks/WorkflowTemplateCatalog.js').then((m) => ({
    default: m.WorkflowTemplateCatalog,
  })),
);
// Sprint 22.1.0: Task Management Module Maturity
const SectionAssignmentsPage = lazy(() =>
  import('./pages/admin/tasks/SectionAssignmentsPage.js').then((m) => ({
    default: m.SectionAssignmentsPage,
  })),
);
const ConsolidationPage = lazy(() =>
  import('./pages/admin/tasks/ConsolidationPage.js').then((m) => ({
    default: m.ConsolidationPage,
  })),
);
const ConflictDetectionPage = lazy(() =>
  import('./pages/admin/tasks/ConflictDetectionPage.js').then((m) => ({
    default: m.ConflictDetectionPage,
  })),
);

// Sprint 28.7.1 Consolidation pages
const ConsolidationCasesPage = lazy(() =>
  import('./pages/admin/consolidation/ConsolidationCasesPage.js').then((m) => ({
    default: m.ConsolidationCasesPage,
  })),
);
const ConsolidationCaseCreatePage = lazy(() =>
  import('./pages/admin/consolidation/ConsolidationCaseCreatePage.js').then((m) => ({
    default: m.ConsolidationCaseCreatePage,
  })),
);
const ConsolidationCaseDetailPage = lazy(() =>
  import('./pages/admin/consolidation/ConsolidationCaseDetailPage.js').then((m) => ({
    default: m.ConsolidationCaseDetailPage,
  })),
);
const SectionInputPage = lazy(() =>
  import('./pages/admin/consolidation/SectionInputPage.js').then((m) => ({
    default: m.SectionInputPage,
  })),
);
const ConsolidationReviewPage = lazy(() =>
  import('./pages/admin/consolidation/ConsolidationReviewPage.js').then((m) => ({
    default: m.ConsolidationReviewPage,
  })),
);
const DossierExportPage = lazy(() =>
  import('./pages/admin/consolidation/DossierExportPage.js').then((m) => ({
    default: m.DossierExportPage,
  })),
);

/** Suspense wrapper for lazy-loaded admin pages */
function Lazy({ C }: { C: React.LazyExoticComponent<any> }) {
  return (
    <Suspense fallback={<SkeletonLoader />}>
      <C />
      <TourRouteCommitMarker />
    </Suspense>
  );
}

/**
 * Layout route that enforces per-route permission checks.
 * Uses the centralized ROUTE_PERMISSION_MAP to look up the required
 * permission for the current URL and renders AccessDeniedPage if denied.
 * Routes without a mapping (Dashboard, Profile, Help) pass through freely.
 */
export function PermissionGate() {
  const location = useLocation();
  const { hasPermission } = usePermissionContext();
  const { isModuleEnabled } = useModuleConfig();

  // Strip the /admin/ prefix to get the relative path for lookup
  const relativePath = location.pathname.replace(/^\/admin\/?/, '');

  // Empty path = dashboard index — always accessible
  if (!relativePath) {
    return <Outlet />;
  }

  // The route's module is checked FIRST (Sprint 63.22.0 gate E12.5 pass 3):
  // the permission check below used to return early for a route with no
  // permission mapping, so a module-gated route with none (organizations,
  // intake-configs, …) rendered while its module was disabled.
  const moduleId = getRouteModule(relativePath);
  if (moduleId && !isModuleEnabled(moduleId)) {
    return <AccessDeniedPage />;
  }

  // No permission mapping = accessible to any session (profile, help). A route
  // may admit any of several permissions (`alsoAdmits`, Sprint 63.23.0 gate
  // E12.5 pass 1); `routeOpensFor` is the question `isLinkVisible` asks too.
  if (!routeOpensFor(relativePath, hasPermission)) {
    return <AccessDeniedPage />;
  }

  return moduleId === 'sector_overlays' ? <SectorOverlayPageGate /> : <Outlet />;
}

function SectorOverlayPageGate() {
  // The navigation endpoint checks the package even for roles that cannot read module settings.
  const { loading, notEntitled, error } = useSectorOverlayNavigation();
  if (loading) return <SkeletonLoader />;
  if (notEntitled || error) return <AccessDeniedPage />;
  return <Outlet />;
}

/**
 * Sprint 13.0.0 — High-contrast theme detector.
 * Sets data-theme="high-contrast" on <html> when:
 *  1. OS prefers-contrast: more, OR
 *  2. User manually toggled it (stored in localStorage key "a11y-high-contrast")
 */
function HighContrastDetector() {
  useEffect(() => {
    const apply = () => {
      const stored = localStorage.getItem('a11y-high-contrast');
      const osPrefers = window.matchMedia('(prefers-contrast: more)').matches;
      if (stored === 'true' || (stored === null && osPrefers)) {
        document.documentElement.setAttribute('data-theme', 'high-contrast');
      } else {
        document.documentElement.removeAttribute('data-theme');
      }
    };

    apply();

    const mq = window.matchMedia('(prefers-contrast: more)');
    mq.addEventListener('change', apply);

    // Listen for manual toggle from ProfilePage (custom event)
    const onToggle = () => apply();
    window.addEventListener('a11y-high-contrast-changed', onToggle);

    return () => {
      mq.removeEventListener('change', apply);
      window.removeEventListener('a11y-high-contrast-changed', onToggle);
    };
  }, []);

  return null;
}

/**
 * Site-level guard — checks wizard status on every page load (public endpoint,
 * no auth required). Redirects to /setup if the wizard is not yet done.
 * Skipped on routes that don't need this check (/setup itself, public intake forms).
 */
function SiteSetupGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);

  const skipRoutes = ['/setup', '/rights-request'];
  const skip = skipRoutes.some((r) => location.pathname.startsWith(r));

  useEffect(() => {
    if (skip) {
      setChecking(false);
      return;
    }
    apiClient
      .getSetupWizard()
      .then((wizard: any) => {
        const done = wizard?.status === 'completed' || wizard?.status === 'skipped';
        setNeedsSetup(!done);
      })
      .catch(() => {
        // API unreachable or wizard not initialised — send to /setup
        setNeedsSetup(true);
      })
      .finally(() => setChecking(false));
  }, [skip]);

  if (skip) return <>{children}</>;
  if (checking) return <SkeletonLoader />;
  if (needsSetup) return <Navigate to="/setup" replace />;
  return <>{children}</>;
}

/**
 * Guards the /setup route — redirects to /login if the wizard is already
 * completed or skipped (so it cannot be re-run after first-run).
 */
function SetupGuard({ children }: { children: React.ReactNode }) {
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    apiClient
      .getSetupWizard()
      .then((wizard: any) => {
        const done = wizard?.status === 'completed' || wizard?.status === 'skipped';
        setAllowed(!done);
      })
      .catch(() => {
        // If fetch fails (e.g. not yet initialised), allow through
        setAllowed(true);
      })
      .finally(() => setChecking(false));
  }, []);

  if (checking) return <SkeletonLoader />;
  if (!allowed) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/**
 * Application root with route definitions.
 * See: docs/product/phases/phase-1/sprint-1.0.0.md - UX Flows
 */
export function App() {
  return (
    <LocaleProvider>
      <HighContrastDetector />
      <MfaStepUpRedirector />
      <PasswordChangeRedirector />
      <ErrorBoundary>
        <ToastProvider>
          <AuthProvider>
            <PermissionProvider>
              <ModuleConfigProvider>
                <SiteSetupGuard>
                  <Routes>
                    {/* Public routes (no auth required) */}
                    <Route path="/login" element={<LoginPage />} />
                    {/*
                      Sprint 63.6.0 — where a federated sign-in lands. Public
                      because it runs before the session exists: it is the page
                      that exchanges the one-time code FOR the session.
                    */}
                    <Route path="/sso/handoff" element={<SsoHandoffPage />} />
                    {/*
                      Sprint 63.7.0 — the forced-enrolment interstitial.

                      Outside `ProtectedRoute` on purpose. The session here is
                      REAL and authenticated; what it is not is usable — the
                      server admits it to six exact paths and nothing else, so a
                      `ProtectedRoute` wrapper would run the wizard read this
                      session cannot make, and then redirect back here from its
                      own guard. The page's own controls are the only things it
                      calls, and each of them is on the admitted list.
                    */}
                    <Route path="/mfa-enrolment-required" element={<MfaEnrolmentRequiredPage />} />
                    {/*
                      Sprint 63.29.0 (#541) — the forced password change. Outside
                      `ProtectedRoute` for the reason the MFA interstitials are:
                      the server admits this session to three paths, and the page
                      calls only those.
                    */}
                    <Route
                      path="/password-change-required"
                      element={<PasswordChangeRequiredPage />}
                    />
                    {/*
                      Sprint 63.6.3, gate E12.5 pass 2 — the code prompt for a
                      LIVE session held at the TOTP step-up (403 MFA_REQUIRED).
                      Outside `ProtectedRoute` for the same reason as the
                      enrolment interstitial: the session is real, the server
                      admits it to three paths, and this page calls only those.
                    */}
                    <Route path="/mfa-step-up" element={<MfaStepUpPage />} />
                    {/*
                      Sprint 63.23.0 — the OAuth consent screen. Outside the
                      admin shell (a single decision, not a workspace) and
                      outside `ProtectedRoute`: the page requires a session
                      itself and, when there is none, sends the visitor through
                      `/login?next=` back to this exact URL — which
                      `ProtectedRoute`'s redirect would drop.
                    */}
                    <Route path="/oauth/consent" element={<OAuthConsentPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/verify-email" element={<VerifyEmailPage />} />
                    <Route path="/rights-request" element={<IntakeFormPage />} />
                    <Route
                      path="/rights-request/confirmation"
                      element={<IntakeConfirmationPage />}
                    />

                    {/* Public first-run setup wizard — blocked once completed or skipped */}
                    <Route
                      path="/setup"
                      element={
                        <SetupGuard>
                          <Suspense fallback={<SkeletonLoader />}>
                            <SetupWizardPage publicMode />
                          </Suspense>
                        </SetupGuard>
                      }
                    />

                    {/* Admin routes (auth required) */}
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute>
                          <AdminLayout />
                        </ProtectedRoute>
                      }
                    >
                      {/* PermissionGate checks per-route permissions via ROUTE_PERMISSION_MAP */}
                      <Route element={<PermissionGate />}>
                        <Route index element={<Lazy C={DashboardPage} />} />
                        {/* Sprint 17.4.0 Task Management routes */}
                        <Route path="tasks" element={<Lazy C={TaskListPage} />} />
                        <Route path="tasks/calendar" element={<Lazy C={TaskCalendarPage} />} />
                        <Route path="tasks/my" element={<Lazy C={MyTasksPage} />} />
                        <Route path="tasks/overdue" element={<Lazy C={OverdueTasksPage} />} />
                        <Route path="tasks/board" element={<Lazy C={TaskBoardPage} />} />
                        <Route path="tasks/workflows" element={<Lazy C={WorkflowListPage} />} />
                        <Route
                          path="tasks/workflows/templates"
                          element={<Lazy C={WorkflowTemplateCatalog} />}
                        />
                        <Route
                          path="tasks/workflows/:id"
                          element={<Lazy C={WorkflowDetailPage} />}
                        />
                        <Route path="tasks/:id" element={<Lazy C={TaskDetailPage} />} />
                        {/* Sprint 22.1.0 Task Management Maturity */}
                        <Route
                          path="tasks/:taskId/sections"
                          element={<Lazy C={SectionAssignmentsPage} />}
                        />
                        <Route
                          path="tasks/:masterTaskId/consolidation"
                          element={<Lazy C={ConsolidationPage} />}
                        />
                        <Route
                          path="tasks/:masterTaskId/conflicts"
                          element={<Lazy C={ConflictDetectionPage} />}
                        />
                        <Route path="rights-requests" element={<Lazy C={TicketListPage} />} />
                        <Route path="rights-requests/new" element={<Lazy C={CreateCasePage} />} />
                        {/* Sprint 40.0.0 Case Import */}
                        <Route
                          path="rights-requests/import"
                          element={<Lazy C={ImportCasesPage} />}
                        />
                        {/* Sprint 48.1.1 (finish) — DSR Batch (Forseti AI workflow). */}
                        <Route
                          path="rights-requests/batch-autocomplete"
                          element={<Lazy C={DsrBatchUploadPage} />}
                        />
                        <Route
                          path="rights-requests/:casePublicId"
                          element={<Lazy C={TicketDetailPage} />}
                        />
                        <Route
                          path="rights-requests/settings/cmp-integration"
                          element={<Lazy C={CmpSyncSettingsPage} />}
                        />
                        {/* Sprint 43.2.0 batch 3 (E1) — DSR Response Templates admin */}
                        <Route
                          path="rights-requests/response-templates"
                          element={<Lazy C={ResponseTemplatesAdminPage} />}
                        />
                        {/* Sprint 40.2.0 Data Subject Profiles */}
                        <Route path="data-subjects" element={<Lazy C={DataSubjectListPage} />} />
                        <Route
                          path="data-subjects/:profileId"
                          element={<Lazy C={DataSubjectDetailPage} />}
                        />
                        {/* Sprint 43.5.1 — `/admin/templates` route removed.
                            Use `/admin/rights-requests/response-templates`
                            (ResponseTemplatesAdminPage) instead. */}
                        <Route path="audit" element={<Lazy C={AuditTrailPage} />} />
                        {/* Sprint 22.0.0 Audit Trail Maturity */}
                        <Route path="audit/unified" element={<Lazy C={UnifiedAuditPage} />} />
                        <Route
                          path="audit/evidence-packs"
                          element={<Lazy C={AuditEvidencePacksPage} />}
                        />
                        {/* Sprint 62.2.5 — cross-framework evidence packs.
                            `/new` is declared BEFORE `/:packId` so the literal
                            wins; declared after, the builder would resolve as a
                            pack whose id is the string "new". */}
                        <Route
                          path="framework-evidence-packs"
                          element={<Lazy C={FrameworkEvidencePackListPage} />}
                        />
                        <Route
                          path="framework-evidence-packs/new"
                          element={<Lazy C={FrameworkEvidencePackBuilderPage} />}
                        />
                        <Route
                          path="framework-evidence-packs/:packId"
                          element={<Lazy C={FrameworkEvidencePackDetailPage} />}
                        />
                        {/* Sprint 62.3.0 — AI system register. `/new` is
                            declared BEFORE `/:profileId` so the literal wins;
                            declared after, the registration form would resolve
                            as a profile whose id is the string "new". */}
                        {/* Sprint 62.48.0 wave 5 - the AI registers. Literal paths, declared before `:profileId`. */}
                        <Route
                          path="ai-governance/inventory-reviews"
                          element={<Lazy C={AiInventoryReviewsRegisterPage} />}
                        />
                        <Route
                          path="ai-governance/payment-authorities"
                          element={<Lazy C={AiPaymentAuthoritiesRegisterPage} />}
                        />
                        <Route
                          path="ai-governance/interventions"
                          element={<Lazy C={AiInterventionsRegisterPage} />}
                        />
                        <Route
                          path="ai-governance/complaints"
                          element={<Lazy C={AiComplaintsRegisterPage} />}
                        />
                        <Route
                          path="ai-governance/framework-and-lines"
                          element={<Lazy C={AiFrameworkLinesRegisterPage} />}
                        />
                        <Route
                          path="ai-governance/board-figures"
                          element={<Lazy C={AiBoardFiguresRegisterPage} />}
                        />
                        <Route path="ai-governance" element={<Lazy C={AiSystemRegisterPage} />} />
                        <Route
                          path="ai-governance/new"
                          element={<Lazy C={AiSystemRegisterFormPage} />}
                        />
                        {/* Sprint 62.26.0 (AC12) — the same form in edit mode,
                            not a second form: it reads `:profileId` and PATCHes
                            only what changed. */}
                        <Route
                          path="ai-governance/:profileId/edit"
                          element={<Lazy C={AiSystemRegisterFormPage} />}
                        />
                        <Route
                          path="ai-governance/:profileId"
                          element={<Lazy C={AiSystemDetailPage} />}
                        />
                        {/* Sprint 62.4.0 — regulatory classification.
                            `offerings`, `new` and `:profileId/edit|assess` are
                            literals declared BEFORE `:profileId`, so a route to
                            the offerings register cannot resolve as a subject
                            profile whose id is the string "offerings". */}
                        <Route
                          path="regulatory-classification"
                          element={<Lazy C={ClassificationSubjectRegisterPage} />}
                        />
                        <Route
                          path="regulatory-classification/new"
                          element={<Lazy C={ClassificationSubjectProfileFormPage} />}
                        />
                        <Route
                          path="regulatory-classification/offerings"
                          element={<Lazy C={RegulatedOfferingsPage} />}
                        />
                        {/* Sprint 62.13.0 — literal segments before :param. */}
                        <Route
                          path="penalty-exposure"
                          element={<Lazy C={PenaltyReadinessPage} />}
                        />
                        <Route
                          path="penalty-exposure/inputs"
                          element={<Lazy C={ExposureInputsPage} />}
                        />
                        <Route
                          path="penalty-exposure/register"
                          element={<Lazy C={EnforcementRegisterPage} />}
                        />
                        <Route
                          path="enforcement-cases"
                          element={<Lazy C={EnforcementCasesPage} />}
                        />
                        <Route
                          path="enforcement-cases/:id"
                          element={<Lazy C={EnforcementCaseDetailPage} />}
                        />
                        {/* Sprint 62.14.0 — literal segments before :param. */}
                        <Route path="authority-directions" element={<Lazy C={DirectionsPage} />} />
                        <Route
                          path="authority-directions/:id"
                          element={<Lazy C={DirectionDetailPage} />}
                        />
                        <Route path="legal-holds" element={<Lazy C={LegalHoldsPage} />} />
                        <Route
                          path="regulated-accounts"
                          element={<Lazy C={RegulatedAccountsPage} />}
                        />
                        <Route
                          path="regulated-accounts/:id"
                          element={<Lazy C={RegulatedAccountDetailPage} />}
                        />
                        <Route
                          path="product-classifications"
                          element={<Lazy C={ProductClassificationsPage} />}
                        />
                        <Route
                          path="conformity-certificates"
                          element={<Lazy C={ConformityCertificatesPage} />}
                        />
                        {/* Sprint 62.20.0 — literal segments before :id. */}
                        <Route
                          path="regulatory-placeholders"
                          element={<Lazy C={RegulatoryPlaceholdersPage} />}
                        />
                        <Route
                          path="regulatory-placeholders/:id"
                          element={<Lazy C={RegulatoryPlaceholderDetailPage} />}
                        />
                        {/* Sprint 62.19.0 — literal segments before :id. */}
                        <Route path="sector-licences" element={<Lazy C={SectorLicencesPage} />} />
                        {/* Sprint 62.54.0 — applications and fee lines; literal segments before :id. */}
                        <Route
                          path="sector-licences/applications"
                          element={<Lazy C={LicenceApplicationsPage} />}
                        />
                        <Route
                          path="sector-licences/applications/:id"
                          element={<Lazy C={LicenceApplicationDetailPage} />}
                        />
                        {/* Sprint 62.55.0 — the period view; a literal segment before :id. */}
                        <Route
                          path="sector-licences/fee-lines"
                          element={<Lazy C={FeeLinesByPeriodPage} />}
                        />
                        <Route
                          path="sector-licences/:id"
                          element={<Lazy C={SectorLicenceDetailPage} />}
                        />
                        <Route
                          path="marketing-approvals"
                          element={<Lazy C={MarketingApprovalsPage} />}
                        />
                        {/* Sprint 62.24.0 — the credit-information supply register; literal segments before :supplyId. */}
                        <Route
                          path="credit-information-supply"
                          element={<Lazy C={CreditInformationSuppliesPage} />}
                        />
                        <Route
                          path="credit-information-supply/:supplyId"
                          element={<Lazy C={CreditInformationSupplyDetailPage} />}
                        />
                        {/* Sprint 62.24.0 — the data-service provider record; literal segments before :providerId. */}
                        <Route
                          path="data-service-providers"
                          element={<Lazy C={DataServiceProvidersPage} />}
                        />
                        <Route
                          path="data-service-providers/:providerId"
                          element={<Lazy C={DataServiceProviderDetailPage} />}
                        />
                        {/* Sprint 62.50.0 — the supervision-information registers; gated on the overlay's read grant by the route table. */}
                        <Route
                          path="pcf-supervision/shared-information"
                          element={<Lazy C={PcfSharedInformationPage} />}
                        />
                        <Route
                          path="pcf-supervision/publicity"
                          element={<Lazy C={PcfBranchPublicityPage} />}
                        />
                        <Route
                          path="pcf-supervision/information-requests"
                          element={<Lazy C={PcfBranchInformationRequestsPage} />}
                        />
                        <Route
                          path="pcf-supervision/depositor-enquiries"
                          element={<Lazy C={PcfDepositorEnquiriesPage} />}
                        />
                        <Route
                          path="pcf-supervision/system-records"
                          element={<Lazy C={PcfSupervisionSystemRecordsPage} />}
                        />
                        {/* Sprint 62.51.0 — the data-exploitation registers; gated on data_exploitation:read by the route table. */}
                        <Route
                          path="data-exploitation/routes"
                          element={<Lazy C={DeExploitationRoutesPage} />}
                        />
                        <Route
                          path="data-exploitation/requests"
                          element={<Lazy C={DeRequestsGrantsPage} />}
                        />
                        <Route
                          path="data-exploitation/testing"
                          element={<Lazy C={DeTestingRecordsPage} />}
                        />
                        <Route
                          path="data-exploitation/notices"
                          element={<Lazy C={DeIncidentNoticesPage} />}
                        />
                        <Route
                          path="data-exploitation/dispositions"
                          element={<Lazy C={DeDispositionsPage} />}
                        />
                        <Route
                          path="data-exploitation/incubator"
                          element={<Lazy C={DeIncubatorRecordsPage} />}
                        />
                        <Route
                          path="data-exploitation/foreign-cooperation"
                          element={<Lazy C={DeForeignCooperationPage} />}
                        />
                        {/* Sprint 62.52.0 — the cybersecurity assurance index registers; gated on cybersecurity_assurance_index:read by the route table. */}
                        <Route
                          path="cybersecurity-assurance-index/periods"
                          element={<Lazy C={CsaiPeriodsPage} />}
                        />
                        <Route
                          path="cybersecurity-assurance-index/periods/:periodId"
                          element={<Lazy C={CsaiPeriodDetailPage} />}
                        />
                        <Route
                          path="cybersecurity-assurance-index/periods/:periodId/systems/:systemEntryId"
                          element={<Lazy C={CsaiSystemItemsPage} />}
                        />
                        <Route
                          path="cybersecurity-assurance-index/statistics"
                          element={<Lazy C={CsaiStatisticsPage} />}
                        />
                        <Route
                          path="cybersecurity-assurance-index/supplier-requests"
                          element={<Lazy C={CsaiSupplierRequestsPage} />}
                        />
                        {/* Sprint 62.49.0 — the food traceability registers; gated on food_traceability:read by the route table. */}
                        <Route
                          path="food-traceability/establishment"
                          element={<Lazy C={FtEstablishmentPage} />}
                        />
                        <Route
                          path="food-traceability/lot-results"
                          element={<Lazy C={FtLotResultsPage} />}
                        />
                        <Route
                          path="food-traceability/inspections"
                          element={<Lazy C={FtInspectionsPage} />}
                        />
                        <Route
                          path="food-traceability/warnings"
                          element={<Lazy C={FtWarningsPage} />}
                        />
                        <Route
                          path="food-traceability/notices"
                          element={<Lazy C={FtNoticesPage} />}
                        />
                        <Route
                          path="food-traceability/decisions"
                          element={<Lazy C={FtDecisionsPage} />}
                        />
                        <Route
                          path="food-traceability/corrections"
                          element={<Lazy C={FtCorrectionsPage} />}
                        />
                        <Route
                          path="food-traceability/complaints"
                          element={<Lazy C={FtComplaintsPage} />}
                        />
                        <Route
                          path="food-traceability/routes"
                          element={<Lazy C={FtConnectionRoutesPage} />}
                        />
                        {/* Sprint 62.56.0 — the biometric capture registers; gated on the overlay's read grant by the route table. */}
                        <Route
                          path="biometric-capture/components"
                          element={<Lazy C={BiometricCaptureComponentsPage} />}
                        />
                        <Route
                          path="biometric-capture/components/:componentId"
                          element={<Lazy C={BiometricCaptureComponentDetailPage} />}
                        />
                        <Route
                          path="biometric-capture/custody-transfers"
                          element={<Lazy C={BiometricCustodyTransfersPage} />}
                        />
                        <Route
                          path="biometric-capture/readiness"
                          element={<Lazy C={BiometricReadinessReviewsPage} />}
                        />
                        <Route
                          path="biometric-capture/supervision-records"
                          element={<Lazy C={BiometricSupervisionRecordsPage} />}
                        />
                        {/* Sprint 63.24.0 Slice A — controlled-testing cases; literal segment before :caseId. */}
                        <Route
                          path="controlled-testing/cases"
                          element={<Lazy C={CaseListPage} />}
                        />
                        <Route
                          path="controlled-testing/cases/:caseId"
                          element={<Lazy C={CaseDetailPage} />}
                        />
                        {/* Sprint 62.19.0 — investor protection; literal segments before :param. */}
                        <Route
                          path="investor-verifications"
                          element={<Lazy C={InvestorVerificationsPage} />}
                        />
                        <Route
                          path="account-data-disclosures"
                          element={<Lazy C={AccountDataDisclosuresPage} />}
                        />
                        <Route
                          path="regulatory-disclosures"
                          element={<Lazy C={RegulatoryDisclosuresPage} />}
                        />
                        <Route
                          path="regulatory-disclosures/:id"
                          element={<Lazy C={RegulatoryDisclosureDetailPage} />}
                        />
                        {/*
                          Sprint 65.1.0 — the compliance work programme. The
                          literal segment goes BEFORE the parameterised one, or
                          `/admin/compliance-operations` would be matched as an
                          obligation whose id is the empty string.
                        */}
                        <Route
                          path="compliance-operations"
                          element={<Lazy C={ObligationListPage} />}
                        />
                        <Route
                          path="compliance-operations/:obligationId"
                          element={<Lazy C={ObligationDetailPage} />}
                        />
                        {/* Sprint 62.18.0 — student records; the literal segment before :recordId. */}
                        <Route path="student-records" element={<Lazy C={StudentRecordsPage} />} />
                        <Route
                          path="student-records/:recordId"
                          element={<Lazy C={StudentRecordDetailPage} />}
                        />
                        {/* Sprint 62.17.0 — publishing; the literal segment before :id. */}
                        <Route path="publications" element={<Lazy C={PublicationsPage} />} />
                        <Route
                          path="publications/:id"
                          element={<Lazy C={PublicationDetailPage} />}
                        />
                        <Route
                          path="legal-representatives"
                          element={<Lazy C={LegalRepresentativesPage} />}
                        />
                        <Route
                          path="publication-assessments"
                          element={<Lazy C={PublicationAssessmentsPage} />}
                        />
                        <Route
                          path="regulatory-classification/offerings/new"
                          element={<Lazy C={RegulatedOfferingFormPage} />}
                        />
                        <Route
                          path="regulatory-classification/offerings/:offeringId"
                          element={<Lazy C={RegulatedOfferingFormPage} />}
                        />
                        <Route
                          path="regulatory-classification/:profileId"
                          element={<Lazy C={ClassificationSubjectDetailPage} />}
                        />
                        <Route
                          path="regulatory-classification/:profileId/edit"
                          element={<Lazy C={ClassificationSubjectProfileFormPage} />}
                        />
                        <Route
                          path="regulatory-classification/:profileId/assess"
                          element={<Lazy C={ClassificationAssessmentPage} />}
                        />
                        {/* Sprint 62.5.0 — trust evidence. Each family declares
                            its list before its `:id` detail, and the four
                            families own disjoint path prefixes, so no detail
                            route can swallow a sibling list. */}
                        <Route
                          path="trust-evidence/identities"
                          element={<Lazy C={TrustIdentityRegisterPage} />}
                        />
                        <Route
                          path="trust-evidence/identities/:profileId"
                          element={<Lazy C={TrustIdentityProfileDetailPage} />}
                        />
                        <Route
                          path="trust-evidence/providers"
                          element={<Lazy C={TrustProvidersPage} />}
                        />
                        <Route
                          path="trust-evidence/credentials/:credentialId"
                          element={<Lazy C={TrustCredentialDetailPage} />}
                        />
                        <Route
                          path="trust-evidence/submissions"
                          element={<Lazy C={ElectronicSubmissionsPage} />}
                        />
                        <Route
                          path="trust-evidence/submissions/:submissionId"
                          element={<Lazy C={ElectronicSubmissionDetailPage} />}
                        />
                        <Route
                          path="trust-evidence/agreements"
                          element={<Lazy C={ElectronicAgreementsPage} />}
                        />
                        <Route
                          path="trust-evidence/agreements/:agreementId"
                          element={<Lazy C={ElectronicAgreementDetailPage} />}
                        />
                        <Route
                          path="trust-evidence/identity-exchanges"
                          element={<Lazy C={IdentityExchangePage} />}
                        />
                        {/* Sprint 62.6.0 — regulatory artifacts. The two literal
                            segments are declared BEFORE `:artifactId` so a
                            reader can see that `grants` and `verification` are
                            pages rather than record identifiers. React Router
                            ranks a static segment above a dynamic one either
                            way; the order is for the next person editing this
                            block, and it mirrors the backend router, where the
                            equivalent ordering IS load-bearing — a shadowed
                            `/grants` there would be handled as a detail read
                            and write a denial row for an artifact that does not
                            exist. */}
                        <Route
                          path="regulatory-artifacts"
                          element={<Lazy C={ArtifactCatalogPage} />}
                        />
                        <Route
                          path="regulatory-artifacts/grants"
                          element={<Lazy C={ArtifactRestrictedAccessPage} />}
                        />
                        <Route
                          path="regulatory-artifacts/verification"
                          element={<Lazy C={ArtifactVerificationQueuePage} />}
                        />
                        <Route
                          path="regulatory-artifacts/:artifactId"
                          element={<Lazy C={ArtifactDetailPage} />}
                        />
                        {/* Sprint 62.7.0 — the three evidence domains. Each
                            family declares its list before its `:id` detail,
                            and the families own disjoint path prefixes, so no
                            detail route can swallow a sibling list. */}
                        <Route
                          path="public-data/datasets"
                          element={<Lazy C={PublicDataDatasetCatalogPage} />}
                        />
                        <Route
                          path="public-data/datasets/:datasetId"
                          element={<Lazy C={PublicDataDatasetDetailPage} />}
                        />
                        <Route
                          path="public-data/sharing"
                          element={<Lazy C={PublicDataSharingRegisterPage} />}
                        />
                        <Route
                          path="public-data/releases"
                          element={<Lazy C={PublicDataOpenDataReleasesPage} />}
                        />
                        <Route
                          path="payments-evidence/services"
                          element={<Lazy C={PaymentServicesPage} />}
                        />
                        <Route
                          path="payments-evidence/verifications"
                          element={<Lazy C={EkycEvidencePage} />}
                        />
                        <Route
                          path="payments-evidence/verifications/:verificationId"
                          element={<Lazy C={EkycVerificationDetailPage} />}
                        />
                        <Route
                          path="payments-evidence/exceptions"
                          element={<Lazy C={PaymentExceptionReviewPage} />}
                        />
                        <Route
                          path="payments-evidence/retention"
                          element={<Lazy C={RetentionReconciliationPage} />}
                        />
                        <Route
                          path="payments-evidence/supervisory-reporting"
                          element={<Lazy C={SupervisoryReportingPage} />}
                        />
                        <Route
                          path="payments-evidence/aml-programme"
                          element={<Lazy C={AmlProgrammePage} />}
                        />
                        <Route
                          path="payments-evidence/aml-cases"
                          element={<Lazy C={AmlCasesPage} />}
                        />
                        <Route
                          path="payments-evidence/aml-cases/:caseId"
                          element={<Lazy C={AmlCaseDetailPage} />}
                        />
                        <Route
                          path="platform-evidence/sellers"
                          element={<Lazy C={PlatformSellerRegisterPage} />}
                        />
                        <Route
                          path="platform-evidence/reconciliation"
                          element={<Lazy C={PlatformTransactionReconciliationPage} />}
                        />
                        <Route
                          path="platform-evidence/authority-requests"
                          element={<Lazy C={PlatformAuthorityCorrespondencePage} />}
                        />
                        {/* Sprint 62.8.0 — manufacturing operations. One path
                            family (`manufacturing/…`) for nine leaves that live
                            under two different owning modules in the sidebar: the
                            route prefix follows the API namespace, not the
                            navigation, because the product master is shared and a
                            split prefix would make the two OT pages look like a
                            separate module. No `:id` detail routes here — every
                            surface is a register with inline detail, so nothing
                            can swallow a sibling list. */}
                        <Route
                          path="manufacturing/products"
                          element={<Lazy C={ManufacturingProductRegisterPage} />}
                        />
                        <Route
                          path="manufacturing/batches"
                          element={<Lazy C={ManufacturingBatchRegisterPage} />}
                        />
                        <Route
                          path="manufacturing/trace"
                          element={<Lazy C={ManufacturingTraceTimelinePage} />}
                        />
                        <Route
                          path="manufacturing/conformity"
                          element={<Lazy C={ManufacturingConformityEvidencePage} />}
                        />
                        <Route
                          path="manufacturing/capa-recall"
                          element={<Lazy C={ManufacturingCapaRecallPage} />}
                        />
                        <Route
                          path="manufacturing/epr"
                          element={<Lazy C={ManufacturingEprReconciliationPage} />}
                        />
                        <Route
                          path="manufacturing/chemicals"
                          element={<Lazy C={ManufacturingChemicalRegisterPage} />}
                        />
                        <Route
                          path="manufacturing/chemical-traceability"
                          element={<Lazy C={ManufacturingChemicalTraceabilityPage} />}
                        />
                        <Route
                          path="manufacturing/ot-assets"
                          element={<Lazy C={ManufacturingOtAssetProfilesPage} />}
                        />
                        <Route
                          path="manufacturing/remote-access"
                          element={<Lazy C={ManufacturingRemoteAccessAssurancePage} />}
                        />
                        {/* Sprint 62.9.0 — logistics, customs, parcel and
                            delivery. Seven flat leaves; no `:id` detail route,
                            because every register on these pages is read and
                            amended in place and a bare segment before a sibling
                            list is how a dynamic route swallows one. */}
                        <Route
                          path="logistics/shipments"
                          element={<Lazy C={LogisticsShipmentRegisterPage} />}
                        />
                        <Route
                          path="logistics/customs"
                          element={<Lazy C={LogisticsCustomsEvidencePage} />}
                        />
                        <Route
                          path="logistics/inspections"
                          element={<Lazy C={LogisticsInspectionsHoldsPage} />}
                        />
                        <Route
                          path="logistics/parcels"
                          element={<Lazy C={LogisticsParcelRegisterPage} />}
                        />
                        <Route
                          path="logistics/delivery"
                          element={<Lazy C={LogisticsDeliveryEvidencePage} />}
                        />
                        <Route path="logistics/claims" element={<Lazy C={LogisticsClaimsPage} />} />
                        <Route
                          path="logistics/authorisations"
                          element={<Lazy C={LogisticsPostalAuthorisationsPage} />}
                        />
                        {/* Sprint 62.55.0 — the customs tax-administration registers; each gated on
                            `customs_tax_administration:read` by the route table. */}
                        <Route
                          path="logistics/tax-dossiers"
                          element={<Lazy C={CustomsTaxDossiersPage} />}
                        />
                        <Route
                          path="logistics/tax-payments"
                          element={<Lazy C={CustomsTaxPaymentsPage} />}
                        />
                        <Route
                          path="logistics/duty-free-lists"
                          element={<Lazy C={CustomsTaxDutyFreeListsPage} />}
                        />
                        <Route
                          path="logistics/tax-examinations"
                          element={<Lazy C={CustomsTaxExaminationsPage} />}
                        />
                        <Route
                          path="logistics/tax-transition"
                          element={<Lazy C={CustomsTaxTransitionPage} />}
                        />
                        <Route path="audit/periods" element={<Lazy C={AuditPeriodsPage} />} />
                        <Route path="settings" element={<Lazy C={SettingsPage} />} />
                        {/* Sprint 3.0.0 Users, Roles, Profile routes */}
                        <Route path="profile" element={<Lazy C={ProfilePage} />} />
                        <Route
                          path="platform/notification-preferences"
                          element={<Lazy C={NotificationPreferencesPage} />}
                        />
                        <Route
                          path="platform/notification-admin-queue"
                          element={<Lazy C={NotificationAdminQueuePage} />}
                        />
                        <Route path="users" element={<Lazy C={UsersPage} />} />
                        <Route path="users/:id" element={<Lazy C={UserDetailPage} />} />
                        <Route path="roles" element={<Lazy C={RolesPage} />} />
                        <Route path="roles/:id" element={<Lazy C={RoleDetailPage} />} />
                        {/* Sprint 3.1.0: MFA & SSO */}
                        <Route path="security/mfa" element={<Lazy C={MfaSettingsPage} />} />
                        <Route path="security/sso" element={<Lazy C={SsoConfigPage} />} />
                        {/* Sprint 3.2.0: API Keys, Delegations, Break-Glass, Sessions */}
                        <Route path="security/api-keys" element={<Lazy C={ApiKeysPage} />} />
                        <Route
                          path="security/service-accounts"
                          element={<Lazy C={ServiceAccountsPage} />}
                        />
                        <Route
                          path="security/ip-allowlist"
                          element={<Lazy C={IpAllowlistPage} />}
                        />
                        {/* Sprint 46.2.0 — org sectors picker (AC-5 FE) */}
                        <Route path="settings/sectors" element={<Lazy C={OrgSectorsPage} />} />
                        <Route path="security/delegations" element={<Lazy C={DelegationsPage} />} />
                        <Route path="security/break-glass" element={<Lazy C={BreakGlassPage} />} />
                        <Route
                          path="security/session-settings"
                          element={<Lazy C={SessionSettingsPage} />}
                        />
                        <Route
                          path="security/active-sessions"
                          element={<Lazy C={ActiveSessionsPage} />}
                        />
                        {/* Sprint 3.3.0: Access Review, Attestation, SoD */}
                        <Route path="access-reviews" element={<Lazy C={AccessReviewListPage} />} />
                        <Route
                          path="access-reviews/new"
                          element={<Lazy C={CreateCampaignPage} />}
                        />
                        <Route
                          path="access-reviews/my-reviews"
                          element={<Lazy C={MyReviewsPage} />}
                        />
                        <Route
                          path="access-reviews/analytics"
                          element={<Lazy C={RoleAnalyticsPage} />}
                        />
                        <Route
                          path="access-reviews/:id"
                          element={<Lazy C={CampaignDetailPage} />}
                        />
                        <Route
                          path="attestation-schedules"
                          element={<Lazy C={AttestationSchedulesPage} />}
                        />
                        <Route
                          path="attestation-schedules/new"
                          element={<Lazy C={CreateSchedulePage} />}
                        />
                        <Route
                          path="attestation-schedules/:id/edit"
                          element={<Lazy C={EditSchedulePage} />}
                        />
                        <Route
                          path="attestation-schedules/:id/history"
                          element={<Lazy C={ScheduleHistoryPage} />}
                        />
                        <Route path="sod-rules" element={<Lazy C={SodRulesListPage} />} />
                        <Route path="sod-rules/new" element={<Lazy C={CreateSodRulePage} />} />
                        <Route
                          path="sod-rules/violations"
                          element={<Lazy C={SodViolationsPage} />}
                        />
                        <Route path="sod-rules/:id/edit" element={<Lazy C={EditSodRulePage} />} />
                        <Route
                          path="sod-rules/:ruleId/exceptions"
                          element={<Lazy C={SodExceptionsPage} />}
                        />
                        {/* Sprint 1.2.0 Data Mapping routes */}
                        <Route path="data-mapping/systems" element={<Lazy C={SystemsPage} />} />
                        <Route
                          path="data-mapping/categories"
                          element={<Lazy C={CategoriesPage} />}
                        />
                        <Route path="data-mapping/registry" element={<Lazy C={RegistryPage} />} />
                        <Route
                          path="data-mapping/registry/:registry_item_id"
                          element={<Lazy C={RegistryPage} />}
                        />
                        <Route path="data-mapping/purposes" element={<Lazy C={PurposesPage} />} />
                        <Route path="data-mapping/retention" element={<Lazy C={RetentionPage} />} />
                        <Route path="data-mapping/flows" element={<Lazy C={FlowsPage} />} />
                        <Route
                          path="data-mapping/activities"
                          element={<Lazy C={ActivitiesPage} />}
                        />
                        {/* Sprint 46.0.0 — ROPA Completeness Report */}
                        <Route
                          path="data-mapping/ropa-completeness"
                          element={<Lazy C={RopaCompletenessPage} />}
                        />
                        <Route path="data-mapping/evidence" element={<Lazy C={EvidencePage} />} />
                        <Route path="data-mapping/exports" element={<Lazy C={ExportsPage} />} />
                        {/* Sprint 20.5.0 Data Mapping Maturity routes */}
                        <Route
                          path="data-mapping/attestation"
                          element={<Lazy C={AttestationCyclePage} />}
                        />
                        <Route
                          path="data-mapping/governance"
                          element={<Lazy C={GovernanceAssignmentsPage} />}
                        />
                        {/* Sprint 27.8.1 Data Discovery Workflow routes */}
                        <Route
                          path="data-mapping/review"
                          element={<Lazy C={DataMappingReviewPage} />}
                        />
                        <Route
                          path="data-mapping/inventory"
                          element={<Lazy C={DataMappingInventoryPage} />}
                        />
                        <Route
                          path="data-mapping/exceptions"
                          element={<Lazy C={DataMappingExceptionsPage} />}
                        />
                        <Route
                          path="data-mapping/history"
                          element={<Lazy C={DataMappingHistoryPage} />}
                        />
                        {/* Sprint 1.3.0 Governance routes */}
                        <Route path="governance/vendors" element={<Lazy C={VendorsPage} />} />
                        <Route
                          path="governance/subprocessors"
                          element={<Lazy C={SubProcessorsPage} />}
                        />
                        <Route
                          path="governance/cross-border"
                          element={<Lazy C={CrossBorderPage} />}
                        />
                        <Route
                          path="governance/cross-border/filings/:filingId"
                          element={<Lazy C={CrossBorderFilingDetailPage} />}
                        />
                        <Route path="governance/drift" element={<Lazy C={DriftMonitorPage} />} />
                        <Route
                          path="governance/scores"
                          element={<Lazy C={ComplianceScorePage} />}
                        />
                        <Route path="governance/playbooks" element={<Lazy C={PlaybooksPage} />} />
                        <Route path="governance/approvals" element={<Lazy C={ApprovalsPage} />} />
                        <Route
                          path="governance/evidence"
                          element={<Lazy C={GovernanceEvidencePage} />}
                        />
                        {/* Sprint 41.2.0 E6 — Governance Audit Trail */}
                        <Route
                          path="governance/audit-trail"
                          element={<Lazy C={GovernanceAuditPage} />}
                        />
                        <Route
                          path="governance/board-report"
                          element={<Lazy C={BoardReportPage} />}
                        />
                        {/* Sprint 41.5.0 G4.5 — Deprecated governance pages redirect to canonical vendor-risk pages. */}
                        <Route
                          path="governance/onboarding"
                          element={
                            <DeprecatedGovernanceRedirect
                              to="/admin/vendor-risk/onboarding-cases"
                              redirectKey="onboarding"
                            />
                          }
                        />
                        <Route
                          path="governance/dpa-reviews"
                          element={
                            <DeprecatedGovernanceRedirect
                              to="/admin/vendor-risk/onboarding-cases"
                              redirectKey="dpa_reviews"
                            />
                          }
                        />
                        <Route
                          path="governance/periodic-reviews"
                          element={<Lazy C={PeriodicReviewPage} />}
                        />
                        <Route
                          path="governance/offboarding"
                          element={<Lazy C={OffboardingPage} />}
                        />
                        {/* Sprint 1.4.0 Consent routes */}
                        <Route path="consent/policies" element={<Lazy C={PoliciesPage} />} />
                        <Route path="consent/purposes" element={<Lazy C={PurposeCatalogPage} />} />
                        {/* Sprint 48.1.4 — Consent gap analysis Forseti workflow */}
                        <Route
                          path="consent/gap-analysis"
                          element={<Lazy C={ConsentGapAnalysisPage} />}
                        />
                        <Route path="consent/workflows" element={<Lazy C={WorkflowsPage} />} />
                        <Route path="consent/snapshots" element={<Lazy C={SnapshotsPage} />} />
                        <Route
                          path="consent/change-awareness"
                          element={<Lazy C={ChangeAwarenessPage} />}
                        />
                        <Route path="consent/audit" element={<Lazy C={ConsentAuditPage} />} />
                        {/* Sprint 4.2.0 CMP Consent Integration routes */}
                        <Route
                          path="consent/cmp-integration"
                          element={<Lazy C={CmpIntegrationPage} />}
                        />
                        <Route
                          path="consent/cmp-integration/webhook"
                          element={<Lazy C={WebhookConfigPage} />}
                        />
                        <Route
                          path="consent/cmp-integration/purpose-mapping"
                          element={<Lazy C={PurposeMappingPage} />}
                        />
                        <Route
                          path="consent/cmp-integration/remap"
                          element={<Lazy C={BulkSyncPage} />}
                        />
                        {/* Sprint 20.6.0 Consent Governance routes */}
                        <Route
                          path="consent/withdrawal-propagation"
                          element={<Lazy C={WithdrawalPropagationPage} />}
                        />
                        {/* Sprint 62.24.0 — consent evidence by recipient class. */}
                        <Route path="consent/evidence" element={<Lazy C={ConsentEvidencePage} />} />
                        {/* Sprint 62.28.0 — marketing-communications registers. */}
                        <Route
                          path="marketing-communications/consents"
                          element={<Lazy C={MarketingConsentPage} />}
                        />
                        <Route
                          path="marketing-communications/suppressions"
                          element={<Lazy C={SuppressionRegisterPage} />}
                        />
                        <Route
                          path="marketing-communications/enforcement"
                          element={<Lazy C={SuppressionEnforcementPage} />}
                        />
                        <Route
                          path="marketing-communications/sender-name-certificates"
                          element={<Lazy C={SenderNameCertificatesPage} />}
                        />
                        <Route
                          path="consent/reconciliation"
                          element={<Lazy C={ReconciliationPage} />}
                        />
                        <Route path="consent/sync" element={<Lazy C={CmpSyncPage} />} />
                        {/* Sprint 40.0.0 Consent Import */}
                        <Route
                          path="consent/workflows/import"
                          element={<Lazy C={ImportWorkflowsPage} />}
                        />
                        {/* Sprint 1.5.0 Assessment routes */}
                        <Route path="assessments/dpias" element={<Lazy C={DpiaListPage} />} />
                        <Route
                          path="assessments/transfers"
                          element={<Lazy C={TransferListPage} />}
                        />
                        <Route
                          path="assessments/dashboard"
                          element={<Lazy C={AssessmentDashboardPage} />}
                        />
                        <Route path="assessments/gap-report" element={<Lazy C={GapReportPage} />} />
                        {/* Sprint 62.35.0 (Slice C) — pre-collection assessments */}
                        <Route
                          path="assessments/pre-collection"
                          element={<Lazy C={PreCollectionAssessmentsPage} />}
                        />
                        {/* Sprint 20.3.0 Dossier Filing routes */}
                        <Route
                          path="assessments/dossier-filings"
                          element={<Lazy C={DossierFilingListPage} />}
                        />
                        <Route
                          path="assessments/dossier-filings/:filingId"
                          element={<Lazy C={DossierFilingDetailPage} />}
                        />
                        {/* Sprint 44.4.0 — Submission artifact health */}
                        <Route
                          path="assessments/dossier-materialisation-health"
                          element={<Lazy C={DossierMaterialisationHealthPage} />}
                        />
                        {/* Sprint 58.4.0 (Phase 25 / Sprint E1a) — PDPL Evidence Pack
                            provider-config wizard. Pack builder + draft pages land
                            in 58.4.1; send page lands in 58.4.2. */}
                        <Route
                          path="pdpl-evidence-packs/email-providers"
                          element={<Lazy C={EmailProviderListPage} />}
                        />
                        {/* Sprint 58.4.1 (Phase 25 / Sprint E1b) — Pack Builder + Draft + Detail. */}
                        <Route
                          path="pdpl-evidence-packs/builder/:dossierId"
                          element={<Lazy C={PdplPackBuilderPage} />}
                        />
                        <Route
                          path="pdpl-evidence-packs/packs/:packId"
                          element={<Lazy C={PdplPackDetailPage} />}
                        />
                        <Route
                          path="pdpl-evidence-packs/packs/:packId/draft"
                          element={<Lazy C={PdplEmailDraftPage} />}
                        />
                        {/* Sprint 1.6.0 Classification routes */}
                        <Route
                          path="classification/taxonomies"
                          element={<Lazy C={TaxonomiesPage} />}
                        />
                        <Route path="classification/labeling" element={<Lazy C={LabelingPage} />} />
                        <Route
                          path="classification/lifecycle"
                          element={<Lazy C={LifecyclePage} />}
                        />
                        <Route
                          path="classification/gaps"
                          element={<Lazy C={ClassificationGapReportPage} />}
                        />
                        {/* Sprint 20.8.0 Classification Maturity routes */}
                        <Route
                          path="classification/review-tasks"
                          element={<Lazy C={ReviewTasksPage} />}
                        />
                        <Route
                          path="classification/exceptions"
                          element={<Lazy C={ExceptionQueuePage} />}
                        />
                        <Route
                          path="classification/enterprise-map"
                          element={<Lazy C={EnterpriseDataMapPage} />}
                        />
                        <Route
                          path="classification/discovery-cycles"
                          element={<Lazy C={DiscoveryCyclesPage} />}
                        />
                        <Route
                          path="classification/exports"
                          element={<Lazy C={ClassificationExportsPage} />}
                        />
                        {/* Sprint 1.7.0 Monitoring routes */}
                        <Route path="monitoring/programs" element={<Lazy C={ProgramsPage} />} />
                        <Route path="monitoring/periods" element={<Lazy C={PeriodsPage} />} />
                        <Route
                          path="monitoring/task-templates"
                          element={<Lazy C={TaskTemplatesPage} />}
                        />
                        <Route path="monitoring/tasks" element={<Lazy C={TasksPage} />} />
                        <Route path="monitoring/kpi" element={<Lazy C={KpiDashboardPage} />} />
                        <Route path="monitoring/ric" element={<Lazy C={RicCasesPage} />} />
                        {/* Sprint 21.3.0 Monitoring Maturity routes */}
                        <Route
                          path="monitoring/framework-link"
                          element={<Lazy C={FrameworkLinkPage} />}
                        />
                        <Route
                          path="monitoring/authority-readiness"
                          element={<Lazy C={AuthorityReadinessPage} />}
                        />
                        <Route
                          path="monitoring/change-review"
                          element={<Lazy C={ChangeReviewPage} />}
                        />
                        <Route path="monitoring/raci" element={<Lazy C={GovernanceRaciPage} />} />
                        {/* Sprint 1.8.0 Incident routes */}
                        <Route path="incidents" element={<Lazy C={IncidentListPage} />} />
                        <Route path="incidents/tasks" element={<Lazy C={IncidentTasksPage} />} />
                        <Route
                          path="incidents/reports"
                          element={<Lazy C={IncidentReportsPage} />}
                        />
                        <Route path="incidents/:id" element={<Lazy C={IncidentDetailPage} />} />
                        {/* Sprint 21.1.0 Incident Response Maturity routes */}
                        <Route
                          path="incidents/breach-notifications"
                          element={<Lazy C={BreachNotificationsPage} />}
                        />
                        <Route
                          path="incidents/supplements"
                          element={<Lazy C={SupplementsPage} />}
                        />
                        <Route
                          path="incidents/remediation"
                          element={<Lazy C={RemediationPage} />}
                        />
                        <Route
                          path="incidents/authority-requests"
                          element={<Lazy C={AuthorityRequestsPage} />}
                        />
                        <Route
                          path="incidents/disclosure-register"
                          element={<Lazy C={DisclosureRegisterPage} />}
                        />
                        {/* Sprint 2.0.0 Connector routes */}
                        <Route
                          path="connectors/definitions"
                          element={<Lazy C={ConnectorDefinitionsPage} />}
                        />
                        <Route path="connectors/instances" element={<Lazy C={ConnectorsPage} />} />
                        <Route
                          path="connectors/credentials"
                          element={<Lazy C={CredentialsPage} />}
                        />
                        <Route path="connectors/event-bus" element={<Lazy C={EventBusPage} />} />
                        <Route path="connectors/brds" element={<Lazy C={BrdCapturePage} />} />
                        <Route
                          path="connectors/security-reviews"
                          element={<Lazy C={SecurityReviewsPage} />}
                        />
                        <Route
                          path="connectors/poc-scorecards"
                          element={<Lazy C={PocScorecardsPage} />}
                        />
                        <Route
                          path="connectors/automation-planning"
                          element={<Lazy C={AutomationPlanningPage} />}
                        />
                        <Route
                          path="connectors/system-inventory"
                          element={<Lazy C={SystemInventoryPage} />}
                        />
                        <Route
                          path="connectors/traceability-matrix"
                          element={<Lazy C={TraceabilityMatrixPage} />}
                        />
                        <Route
                          path="connectors/gap-register"
                          element={<Lazy C={GapRegisterPage} />}
                        />
                        {/* Sprint 2.1.0 Vendor Risk routes */}
                        <Route
                          path="vendor-risk/dashboard"
                          element={<Lazy C={VendorRiskDashboardPage} />}
                        />
                        <Route path="vendor-risk/scoring" element={<Lazy C={RiskScoringPage} />} />
                        <Route
                          path="vendor-risk/questionnaires"
                          element={<Lazy C={VRQuestionnairesPage} />}
                        />
                        <Route
                          path="vendor-risk/questionnaires/templates/:id/edit"
                          element={<Lazy C={VRQuestionnaireTemplateEditor} />}
                        />
                        <Route
                          path="vendor-risk/questionnaires/templates/:id/preview"
                          element={<Lazy C={VRQuestionnaireTemplatePreview} />}
                        />
                        <Route path="contracts" element={<Lazy C={ContractsPage} />} />
                        <Route
                          path="contracts/clause-library"
                          element={<Lazy C={ClauseLibraryPage} />}
                        />
                        <Route
                          path="vendor-risk/gates"
                          element={<Lazy C={GateEvaluationsPage} />}
                        />
                        <Route
                          path="vendor-risk/reviews"
                          element={<Lazy C={VendorReviewsPage} />}
                        />
                        <Route path="vendor-risk/signals" element={<Lazy C={RiskSignalsPage} />} />
                        <Route
                          path="vendor-risk/reassessments"
                          element={<Lazy C={ReassessmentsPage} />}
                        />
                        <Route
                          path="vendor-risk/portfolio"
                          element={<Lazy C={RiskPortfolioPage} />}
                        />
                        <Route
                          path="vendor-risk/acceptances"
                          element={<Lazy C={RiskAcceptancePage} />}
                        />
                        <Route
                          path="vendor-risk/review-routing"
                          element={<Lazy C={ReviewRoutingPage} />}
                        />
                        {/* Sprint 28.3.1 Vendor Onboarding Lifecycle routes */}
                        <Route
                          path="vendor-risk/onboarding-cases"
                          element={<Lazy C={VendorOnboardingCasePage} />}
                        />
                        <Route
                          path="vendor-risk/sub-processor-register"
                          element={<Lazy C={VendorSubProcessorRegisterPage} />}
                        />
                        {/* Sprint 2.2.0 Deletion Orchestration routes */}
                        <Route
                          path="deletion-orchestration/dashboard"
                          element={<Lazy C={OrchestrationDashboardPage} />}
                        />
                        <Route
                          path="deletion-orchestration/runs"
                          element={<Lazy C={OrchestrationRunsPage} />}
                        />
                        <Route
                          path="deletion-orchestration/tasks"
                          element={<Lazy C={ExecutionTasksPage} />}
                        />
                        <Route
                          path="deletion-orchestration/templates"
                          element={<Lazy C={ChecklistTemplatesPage} />}
                        />
                        <Route
                          path="deletion-orchestration/vendor-requests"
                          element={<Lazy C={VendorDeletionRequestsPage} />}
                        />
                        <Route
                          path="deletion-orchestration/proof-chain"
                          element={<Lazy C={ProofChainPage} />}
                        />
                        {/* Sprint 21.5.0 Deletion Orchestration Maturity routes */}
                        <Route
                          path="deletion-orchestration/dsr-integration"
                          element={<Lazy C={DsrIntegrationPage} />}
                        />
                        <Route
                          path="deletion-orchestration/vendor-verification"
                          element={<Lazy C={VendorVerificationPage} />}
                        />
                        <Route
                          path="deletion-orchestration/completeness"
                          element={<Lazy C={DeletionCompletenessPage} />}
                        />
                        {/* Sprint 2.3.0 Discovery & Classification routes */}
                        <Route
                          path="discovery/dashboard"
                          element={<Lazy C={DiscoveryDashboardPage} />}
                        />
                        <Route path="discovery/scans" element={<Lazy C={DiscoveryScansPage} />} />
                        <Route path="discovery/staging" element={<Lazy C={StagingAreaPage} />} />
                        <Route path="discovery/dedup-rules" element={<Lazy C={DedupRulesPage} />} />
                        <Route
                          path="discovery/classification-rules"
                          element={<Lazy C={ClassificationRulesPage} />}
                        />
                        <Route
                          path="discovery/suggestions"
                          element={<Lazy C={SuggestionQueuePage} />}
                        />
                        <Route
                          path="discovery/analytics"
                          element={<Lazy C={SuggestionAnalyticsPage} />}
                        />
                        <Route
                          path="discovery/import-templates"
                          element={<Lazy C={ImportTemplatesPage} />}
                        />
                        <Route
                          path="discovery/connectors"
                          element={<Lazy C={ConnectorManagementPage} />}
                        />
                        {/* Sprint 21.4.0 Data Discovery Maturity routes */}
                        <Route
                          path="discovery/cycles"
                          element={<Lazy C={DiscoveryCyclesMaturityPage} />}
                        />
                        <Route
                          path="discovery/owner-review"
                          element={<Lazy C={OwnerReviewPage} />}
                        />
                        <Route
                          path="discovery/lineage"
                          element={<Lazy C={ClassificationLineagePage} />}
                        />
                        {/* Sprint 2.4.0 Incident Operations Level 2 routes */}
                        <Route
                          path="incident-ops/playbooks"
                          element={<Lazy C={PlaybookTemplatesPage} />}
                        />
                        <Route
                          path="incident-ops/activations"
                          element={<Lazy C={PlaybookActivationsPage} />}
                        />
                        <Route
                          path="incident-ops/sla-definitions"
                          element={<Lazy C={SlaDefinitionsPage} />}
                        />
                        <Route
                          path="incident-ops/sla-dashboard"
                          element={<Lazy C={SlaDashboardPage} />}
                        />
                        <Route
                          path="incident-ops/escalations"
                          element={<Lazy C={EscalationLogPage} />}
                        />
                        <Route
                          path="incident-ops/regulator-exports"
                          element={<Lazy C={RegulatorExportsPage} />}
                        />
                        <Route
                          path="incident-ops/vendor-coordination"
                          element={<Lazy C={VendorCoordinationPage} />}
                        />
                        <Route
                          path="incident-ops/metrics"
                          element={<Lazy C={ResponseMetricsPage} />}
                        />
                        {/* Sprint 21.2.0 Incident Ops Maturity routes */}
                        <Route
                          path="incident-ops/playbook-links"
                          element={<Lazy C={PlaybookLinksPage} />}
                        />
                        <Route
                          path="incident-ops/regulatory-sla"
                          element={<Lazy C={RegulatorySlaPage} />}
                        />
                        <Route
                          path="incident-ops/coordination-dashboard"
                          element={<Lazy C={CoordinationDashboardPage} />}
                        />
                        <Route
                          path="incident-ops/evidence-chain"
                          element={<Lazy C={EvidenceChainPage} />}
                        />
                        <Route
                          path="incident-ops/post-review"
                          element={<Lazy C={PostIncidentReviewPage} />}
                        />
                        {/* Sprint 2.5.0 Program Governance routes */}
                        <Route
                          path="program-governance/health"
                          element={<Lazy C={ProgramHealthPage} />}
                        />
                        <Route
                          path="program-governance/models"
                          element={<Lazy C={MaturityModelsPage} />}
                        />
                        <Route
                          path="program-governance/assessments"
                          element={<Lazy C={MaturityAssessmentsPage} />}
                        />
                        <Route
                          path="program-governance/handbook-assessments"
                          element={<Lazy C={HandbookAssessmentsPage} />}
                        />
                        <Route
                          path="program-governance/trends"
                          element={<Lazy C={TrendDashboardPage} />}
                        />
                        <Route
                          path="program-governance/reporting-periods"
                          element={<Lazy C={ReportingPeriodsPage} />}
                        />
                        <Route
                          path="program-governance/summaries"
                          element={<Lazy C={ExecutiveSummariesPage} />}
                        />
                        <Route
                          path="program-governance/readiness"
                          element={<Lazy C={AuditReadinessPage} />}
                        />
                        <Route
                          path="program-governance/evidence-gaps"
                          element={<Lazy C={EvidenceGapsPage} />}
                        />
                        {/* Sprint 65.8.0 — audit engagements and their findings */}
                        <Route
                          path="program-governance/audit-engagements"
                          element={<Lazy C={AuditEngagementsPage} />}
                        />
                        {/* Sprint 21.7.0 Program Governance Maturity routes */}
                        <Route
                          path="program-governance/obligation-mappings"
                          element={<Lazy C={ObligationMappingsPage} />}
                        />
                        <Route
                          path="program-governance/governance-documents"
                          element={<Lazy C={GovernanceDocumentsPage} />}
                        />
                        <Route
                          path="program-governance/remediation-plans"
                          element={<Lazy C={RemediationPlansPage} />}
                        />
                        {/* Sprint 65.17.0 — ANON-01 de-identification runs. */}
                        <Route
                          path="deidentification/runs"
                          element={<Lazy C={DeidentificationRunsPage} />}
                        />
                        {/* Sprint 65.18.0 — IRP-01 continuity exercises. */}
                        <Route
                          path="continuity/exercises"
                          element={<Lazy C={ContinuityExercisesPage} />}
                        />
                        {/* Sprint 65.19.0 — LEG-01 legal matters. */}
                        <Route
                          path="legal-matters/matters"
                          element={<Lazy C={LegalMattersPage} />}
                        />
                        {/* Sprint 65.21.0 — credit-information uses. */}
                        <Route
                          path="credit-information/uses"
                          element={<Lazy C={CreditInformationUsesPage} />}
                        />
                        {/* Sprint 27.5.1 Governance Model & Document Issuance routes */}
                        <Route
                          path="program-governance/governance-model"
                          element={<Lazy C={GovernanceModelPage} />}
                        />
                        <Route
                          path="program-governance/document-drafting"
                          element={<Lazy C={DocumentDraftingPage} />}
                        />
                        <Route
                          path="program-governance/document-issuance"
                          element={<Lazy C={DocumentIssuancePage} />}
                        />
                        <Route
                          path="program-governance/acknowledgment-tracking"
                          element={<Lazy C={AcknowledgmentTrackingPage} />}
                        />
                        <Route
                          path="program-governance/change-log"
                          element={<Lazy C={ChangeLogPage} />}
                        />
                        <Route
                          path="program-governance/review-cadence"
                          element={<Lazy C={ReviewCadencePage} />}
                        />
                        {/* Sprint 2.6.0 Access Accountability routes */}
                        <Route
                          path="access-accountability/sessions"
                          element={<Lazy C={SessionDashboardPage} />}
                        />
                        <Route
                          path="access-accountability/access-log"
                          element={<Lazy C={AccessLogPage} />}
                        />
                        <Route
                          path="access-accountability/justifications"
                          element={<Lazy C={JustificationReviewPage} />}
                        />
                        <Route
                          path="access-accountability/investigations"
                          element={<Lazy C={InvestigationWorkspacePage} />}
                        />
                        <Route
                          path="access-accountability/patterns"
                          element={<Lazy C={PatternAnalysisPage} />}
                        />
                        <Route
                          path="access-accountability/anchoring"
                          element={<Lazy C={AnchoringStatusPage} />}
                        />
                        <Route
                          path="access-accountability/config"
                          element={<Lazy C={AccessConfigPage} />}
                        />
                        {/* Sprint 21.8.0 Access Accountability Maturity routes */}
                        <Route
                          path="access-accountability/access-policies"
                          element={<Lazy C={AccessPoliciesPage} />}
                        />
                        <Route
                          path="access-accountability/review-campaigns"
                          element={<Lazy C={ReviewCampaignsPage} />}
                        />
                        <Route
                          path="access-accountability/evidence-packages"
                          element={<Lazy C={EvidencePackagesPage} />}
                        />
                        <Route
                          path="access-accountability/evidence-packages/:id"
                          element={<Lazy C={EvidencePackageDetailPage} />}
                        />
                        {/* Sprint 2.7.0 Consent Properties routes */}
                        <Route
                          path="consent-properties/dashboard"
                          element={<Lazy C={CrossPropertyDashboardPage} />}
                        />
                        <Route
                          path="consent-properties/properties"
                          element={<Lazy C={PropertyRegistryPage} />}
                        />
                        <Route
                          path="consent-properties/groups"
                          element={<Lazy C={PropertyGroupsPage} />}
                        />
                        <Route
                          path="consent-properties/bindings"
                          element={<Lazy C={BindingMatrixPage} />}
                        />
                        <Route
                          path="consent-properties/change-requests"
                          element={<Lazy C={ChangeRequestPipelinePage} />}
                        />
                        <Route
                          path="consent-properties/comparison"
                          element={<Lazy C={ComparisonWorkspacePage} />}
                        />
                        <Route
                          path="consent-properties/inconsistencies"
                          element={<Lazy C={InconsistencyListPage} />}
                        />
                        <Route
                          path="consent-properties/snapshot-schedules"
                          element={<Lazy C={SnapshotSchedulesPage} />}
                        />
                        <Route
                          path="consent-properties/golden-templates"
                          element={<Lazy C={GoldenTemplatesPage} />}
                        />
                        <Route
                          path="consent-properties/evidence-packs"
                          element={<Lazy C={EvidencePacksPage} />}
                        />
                        <Route
                          path="consent-properties/audit"
                          element={<Lazy C={ConsentPropertyAuditPage} />}
                        />
                        {/* Sprint 20.7.0 Consent Properties Maturity routes */}
                        <Route
                          path="consent-properties/rollouts"
                          element={<Lazy C={TemplateRolloutsPage} />}
                        />
                        <Route
                          path="consent-properties/analytics"
                          element={<Lazy C={PropertyAnalyticsPage} />}
                        />
                        {/* Sprint 2.8.0 Localization routes */}
                        <Route
                          path="localization/dashboard"
                          element={<Lazy C={LocalizationDashboardPage} />}
                        />
                        <Route
                          path="localization/market-packs"
                          element={<Lazy C={MarketPacksCatalogPage} />}
                        />
                        <Route
                          path="localization/market-packs/:id"
                          element={<Lazy C={PackDetailPage} />}
                        />
                        <Route
                          path="localization/language-packs"
                          element={<Lazy C={LanguagePacksPage} />}
                        />
                        {/* Sprint 45.8.0 - Help-Content Pack admin */}
                        <Route
                          path="localization/help-content-packs"
                          element={<Lazy C={HelpContentPacksPage} />}
                        />
                        <Route
                          path="localization/frameworks"
                          element={<Lazy C={RegulatoryFrameworksPage} />}
                        />
                        {/* Sprint 47.1.0 — SEF URL for framework detail; honours `?tab=`. */}
                        <Route
                          path="localization/frameworks/:frameworkCode"
                          element={<Lazy C={FrameworkDetailPage} />}
                        />
                        {/* Sprint 63.43.0 (AC4) — Sector Overlays Overview; module `sector_overlays`, gated by the route table. */}
                        <Route
                          path="sector-overlays"
                          element={<Lazy C={SectorOverlaysOverviewPage} />}
                        />
                        {/* Sprint 46.2.0 — sector overlay catalog (AC-6 host) */}
                        <Route
                          path="localization/sector-overlays"
                          element={<Lazy C={SectorOverlayCatalogPage} />}
                        />
                        <Route
                          path="localization/mappings"
                          element={<Lazy C={ModuleMappingsPage} />}
                        />
                        <Route
                          path="localization/submissions"
                          element={<Lazy C={PackSubmissionsPage} />}
                        />
                        <Route
                          path="localization/translations"
                          element={<Lazy C={TranslationPreviewPage} />}
                        />
                        <Route
                          path="localization/audit"
                          element={<Lazy C={LocalizationAuditPage} />}
                        />
                        {/* Sprint 22.2.0 Localization Maturity routes */}
                        <Route
                          path="localization/frameworks/:frameworkId/links"
                          element={<Lazy C={FrameworkModuleLinksPage} />}
                        />
                        <Route
                          path="localization/frameworks/:frameworkId/channels"
                          element={<Lazy C={FrameworkChannelsPage} />}
                        />
                        <Route
                          path="localization/regulatory-updates"
                          element={<Lazy C={RegulatoryUpdatesPage} />}
                        />
                        <Route
                          path="localization/regulatory-updates/:updateId"
                          element={<Lazy C={RegulatoryUpdateComparisonPage} />}
                        />
                        {/* Sprint 2.9.0 Partner Marketplace routes */}
                        <Route
                          path="partner-marketplace/dashboard"
                          element={<Lazy C={PartnerDashboardPage} />}
                        />
                        <Route
                          path="partner-marketplace/partner-orgs"
                          element={<Lazy C={PartnerOrgsPage} />}
                        />
                        <Route
                          path="partner-marketplace/service-tiers"
                          element={<Lazy C={ServiceTiersPage} />}
                        />
                        <Route
                          path="partner-marketplace/deliverable-templates"
                          element={<Lazy C={DeliverableTemplatesPage} />}
                        />
                        <Route
                          path="partner-marketplace/deliverables"
                          element={<Lazy C={DeliverablesPage} />}
                        />
                        <Route
                          path="partner-marketplace/brand-profiles"
                          element={<Lazy C={BrandProfilesPage} />}
                        />
                        <Route
                          path="partner-marketplace/support-tickets"
                          element={<Lazy C={SupportTicketsPage} />}
                        />
                        <Route
                          path="partner-marketplace/support-tickets/:ticketId"
                          element={<Lazy C={PartnerTicketDetailPage} />}
                        />
                        <Route
                          path="partner-marketplace/workflow-templates"
                          element={<Lazy C={WorkflowTemplatesPage} />}
                        />
                        <Route
                          path="partner-marketplace/workflows"
                          element={<Lazy C={WorkflowInstancesPage} />}
                        />
                        <Route
                          path="partner-marketplace/escalation"
                          element={<Lazy C={EscalationDashboardPage} />}
                        />
                        <Route
                          path="partner-marketplace/reports"
                          element={<Lazy C={PartnerReportsPage} />}
                        />
                        <Route
                          path="partner-marketplace/audit"
                          element={<Lazy C={PartnerAuditPage} />}
                        />
                        <Route
                          path="partner-marketplace/orgs/:partnerId"
                          element={<Lazy C={PartnerOrgDetailPage} />}
                        />
                        {/* Sprint 1.9.0 Onboarding routes */}
                        <Route path="onboarding/setup" element={<Lazy C={SetupWizardPage} />} />
                        <Route path="onboarding/tours" element={<Lazy C={ToursPage} />} />
                        <Route
                          path="onboarding/sample-data"
                          element={<Lazy C={SampleDataPage} />}
                        />
                        {/* Sprint 22.4.0 Onboarding Maturity routes */}
                        <Route
                          path="onboarding/quick-starts"
                          element={<Lazy C={QuickStartsPage} />}
                        />
                        <Route
                          path="onboarding/progress"
                          element={<Lazy C={OnboardingProgressPage} />}
                        />
                        <Route
                          path="onboarding/personas"
                          element={<Lazy C={PersonaOnboardingPage} />}
                        />
                        <Route
                          path="onboarding/personas/:slug"
                          element={<Lazy C={PersonaDetailPage} />}
                        />
                        <Route
                          path="onboarding/use-cases/:id"
                          element={<Lazy C={UseCaseDetailPage} />}
                        />
                        <Route path="onboarding/help" element={<Lazy C={HelpCenterPage} />} />
                        {/* Sprint 44.1.0 — FAQ editor scaffolding landing */}
                        <Route
                          path="onboarding/help/faq-editor"
                          element={<Lazy C={HelpFaqEditorPage} />}
                        />
                        {/* Help Center is a main module — SEF URLs for articles */}
                        <Route path="help" element={<Lazy C={HelpCenterPage} />} />
                        <Route path="help/:slug" element={<Lazy C={HelpCenterPage} />} />
                        {/* Sprint 3.6.0: Integrations / Settings routes (legacy paths kept for compatibility) */}
                        <Route path="settings/webhooks" element={<Lazy C={WebhooksPage} />} />
                        <Route path="settings/domains" element={<Lazy C={DomainsPage} />} />
                        <Route
                          path="settings/export-schedules"
                          element={<Lazy C={ExportSchedulesPage} />}
                        />
                        <Route path="settings/email" element={<Lazy C={EmailSettingsPage} />} />
                        {/* Sprint 40.1.0: Assessment Trigger Configuration */}
                        <Route
                          path="settings/assessment-triggers"
                          element={<Lazy C={AssessmentTriggersPage} />}
                        />
                        {/* Sprint 4.4.0: Platform-scoped routes for Settings, Integrations, Module Config */}
                        <Route path="platform/modules" element={<Lazy C={ModuleConfigPage} />} />
                        <Route path="platform/settings" element={<Lazy C={SettingsPage} />} />
                        <Route
                          path="platform/organisation-identity"
                          element={<Lazy C={OrgIdentityPage} />}
                        />
                        <Route path="platform/webhooks" element={<Lazy C={WebhooksPage} />} />
                        <Route path="platform/domains" element={<Lazy C={DomainsPage} />} />
                        <Route
                          path="platform/export-schedules"
                          element={<Lazy C={ExportSchedulesPage} />}
                        />
                        <Route path="platform/email" element={<Lazy C={EmailSettingsPage} />} />
                        {/* Sprint 41.4.0: Platform → Files Configuration & Browser */}
                        <Route
                          path="platform/files/config"
                          element={<Lazy C={FilesConfigPage} />}
                        />
                        <Route
                          path="platform/files/browser"
                          element={<Lazy C={FilesBrowserPage} />}
                        />
                        <Route
                          path="platform/files/dashboard"
                          element={<Lazy C={FilesDashboardPage} />}
                        />
                        <Route
                          path="platform/files"
                          element={<Navigate to="/admin/platform/files/dashboard" replace />}
                        />
                        {/* Owner ruling 2026-10-10 — each organisation configures
                            its own AI provider, model and effort (supersedes the
                            47.8.0e single-supplier rule). Gated by
                            `ai_provider:configure`. */}
                        <Route
                          path="forseti/ai-model"
                          element={<Lazy C={ForsetiAiModelSettingsPage} />}
                        />
                        <Route
                          path="forseti/usage"
                          element={<Lazy C={ForsetiUsageDashboardPage} />}
                        />
                        {/* Sprint 47.5.9.6 — Aesir-internal admin
                            reconciliation drift report. Deep-link only;
                            not surfaced in the sidebar (gated by
                            `ai_provider:quota:override`). */}
                        <Route
                          path="forseti/usage/reconciliation"
                          element={<Lazy C={ForsetiReconciliationPage} />}
                        />
                        {/* Sprint 47.9.7 — MCP connector admin (Phase 1
                            read-only). Permission-gated by
                            `ai_provider:mcp:list`. */}
                        <Route path="forseti/mcp-keys" element={<Lazy C={ForsetiMcpKeysPage} />} />
                        {/* Sprint 48.0.0c — Forseti memory browse / curate. */}
                        <Route
                          path="forseti/memory"
                          element={<Lazy C={ForsetiMemoryBrowsePage} />}
                        />
                        {/* Sprint 48.2.8 — Compliance rule auto-mapping. */}
                        <Route path="forseti/rule-mapping" element={<Lazy C={RuleMappingPage} />} />
                        {/* Sprint 48.3.0 — MCP pending writes (Phase 2 admin queue). */}
                        <Route
                          path="forseti/mcp-pending"
                          element={<Lazy C={McpPendingWritesPage} />}
                        />
                        {/* Sprint 48.2.9 — Forseti audit walkthrough + privilege creep. */}
                        <Route
                          path="forseti/audit-walkthrough"
                          element={<Lazy C={AuditWalkthroughPage} />}
                        />
                        <Route
                          path="forseti/privilege-creep"
                          element={<Lazy C={PrivilegeCreepFindingsPage} />}
                        />
                        <Route
                          path="ai-advisor/usage"
                          element={<Navigate to="/admin/forseti/usage" replace />}
                        />
                        {/* Sprint 47.5.8 — Advisor audit lineage (canonical + audit-module alias) */}
                        <Route
                          path="forseti/audit-lineage"
                          element={<Lazy C={AdvisorLineagePage} />}
                        />
                        <Route
                          path="audit/advisor-lineage"
                          element={<Lazy C={AdvisorLineagePage} />}
                        />
                        {/* Sprint 47.7.3 — cross-framework conflict detection. */}
                        <Route
                          path="forseti/cross-framework-conflicts"
                          element={<Lazy C={ForsetiCrossFrameworkConflictsPage} />}
                        />
                        {/* Sprint 47.5.8.6 — AI evidence review queue. */}
                        <Route
                          path="forseti/draft-evidence"
                          element={<Lazy C={DraftEvidencePage} />}
                        />
                        {/* Sprint 47.5.2: Forseti Projects (overview + detail) */}
                        <Route
                          path="forseti/projects"
                          element={<Lazy C={ForsetiProjectsOverviewPage} />}
                        />
                        <Route
                          path="forseti/projects/:id"
                          element={<Lazy C={ForsetiProjectDetailPage} />}
                        />
                        {/* Sprint 47.5.3a: Forseti Document Review (versions + AI edits) */}
                        {/* Sprint 47.8.0a: index route replaces the blank screen at the bare path. */}
                        <Route
                          path="forseti/document-review"
                          element={<Lazy C={ForsetiDocumentReviewIndexPage} />}
                        />
                        <Route
                          path="forseti/document-review/:attachmentId"
                          element={<Lazy C={ForsetiDocumentReviewPage} />}
                        />
                        {/* Sprint 47.5.4a: Forseti chat shell (read-only). Streaming + tool calls land in 47.5.4b. */}
                        <Route path="forseti" element={<Lazy C={ForsetiOverviewPage} />} />
                        <Route
                          path="ai-advisor"
                          element={<Navigate to="/admin/forseti" replace />}
                        />
                        <Route
                          path="forseti/chats/:id"
                          element={<Lazy C={ForsetiChatDetailPage} />}
                        />
                        {/* Sprint 47.5.5: Forseti AI Workflows (list + detail). */}
                        <Route
                          path="forseti/workflows"
                          element={<Lazy C={ForsetiWorkflowListPage} />}
                        />
                        <Route
                          path="forseti/workflows/:id"
                          element={<Lazy C={ForsetiWorkflowDetailPage} />}
                        />
                        {/* Sprint 47.5.6: Forseti Tabular Reviews. */}
                        <Route
                          path="forseti/tabular-reviews"
                          element={<Lazy C={ForsetiTabularReviewsListPage} />}
                        />
                        {/* Sprint 47.7.2: cross-review comparison. Order matters —
                          static route before parametrised :id. */}
                        <Route
                          path="forseti/tabular-reviews/compare"
                          element={<Lazy C={ForsetiTabularReviewsComparePage} />}
                        />
                        <Route
                          path="forseti/tabular-reviews/:id"
                          element={<Lazy C={ForsetiTabularReviewDetailPage} />}
                        />
                        <Route
                          path="tabular-reviews"
                          element={<Navigate to="/admin/forseti/tabular-reviews" replace />}
                        />
                        {/* Sprint 47.4.0: Platform → Immutable Audit Trails (foundation shells) */}
                        <Route
                          path="platform/immutable-audit-trails/settings"
                          element={<Lazy C={IatSettingsPage} />}
                        />
                        <Route
                          path="platform/immutable-audit-trails/status"
                          element={<Lazy C={IatProofStatusPage} />}
                        />
                        <Route
                          path="platform/immutable-audit-trails/integrity-center"
                          element={<Lazy C={IatIntegrityCenterPage} />}
                        />
                        <Route
                          path="platform/immutable-audit-trails/usage"
                          element={<Lazy C={IatUsagePage} />}
                        />
                        {/* Sprint 18.0.0: License & Package Management */}
                        <Route path="settings/license" element={<Lazy C={LicenseSettingsPage} />} />
                        {/* Sprint 7.6.0 Form Engine routes */}
                        <Route path="forms" element={<Lazy C={FormsDashboardPage} />} />
                        <Route path="forms/templates" element={<Lazy C={TemplateBrowserPage} />} />
                        <Route
                          path="forms/fill/:templateId"
                          element={<Lazy C={FormFillerPage} />}
                        />
                        <Route
                          path="forms/instances/:id"
                          element={<Lazy C={FormInstanceDetailPage} />}
                        />
                        <Route
                          path="forms/instances/:id/outputs"
                          element={<Lazy C={FormOutputsPage} />}
                        />
                        {/* Sprint 10.6.0 Form Catalog */}
                        <Route path="forms/catalog" element={<Lazy C={FormCatalogPage} />} />
                        {/* Sprint 21.9.0 Compliance Forms Maturity */}
                        <Route
                          path="forms/filing-case-links"
                          element={<Lazy C={FilingCaseLinksPage} />}
                        />
                        <Route
                          path="forms/section-routing"
                          element={<Lazy C={SectionRoutingPage} />}
                        />
                        <Route
                          path="forms/submission-packages"
                          element={<Lazy C={SubmissionPackagesPage} />}
                        />
                        {/* Sprint 11.5.0 Org Groups routes (Phase 9: Corporate Governance) */}
                        {/*
                          Sprint 65.6.0 (AC3) — DT-01.

                          Both paths, not just the list: `TaskSourceLink`
                          navigates to `${baseRoute}/${entity_id}`, so a route
                          declared only at `/admin/training` would have left the
                          task link resolving to a 404 — the dead-route defect
                          moved one segment along rather than fixed.
                        */}
                        <Route path="training" element={<Lazy C={TrainingPage} />} />
                        <Route path="training/:assignmentId" element={<Lazy C={TrainingPage} />} />
                        {/*
                          Sprint 65.6.0 (AC8) — DPO-01 and PUB-01.

                          Two leaf routes under one `privacy-office` prefix, so
                          `ROUTE_PERMISSION_MAP` can gate each on its own
                          register's `:read` token. A single `/admin/privacy-office`
                          would have had to pick one of the two permissions and
                          would then have hidden the other register from a
                          principal entitled to it.
                        */}
                        <Route
                          path="privacy-office/appointments"
                          element={<Lazy C={DpoAppointmentsPage} />}
                        />
                        <Route
                          path="privacy-office/notices"
                          element={<Lazy C={PrivacyNoticesPage} />}
                        />
                        {/* Sprint 62.57.0 Slice E (D67) — the processing-service certificate register. */}
                        <Route
                          path="privacy-office/service-certificates"
                          element={<Lazy C={ServiceCertificateRegisterPage} />}
                        />
                        <Route
                          path="privacy-office/service-certificates/certificates/:id"
                          element={<Lazy C={ServiceCertificateDetailPage} />}
                        />
                        <Route
                          path="privacy-office/service-certificates/applications/:id"
                          element={<Lazy C={ServiceCertificateApplicationDetailPage} />}
                        />
                        <Route path="org-groups" element={<Lazy C={OrgGroupsPage} />} />
                        <Route
                          path="org-groups/:groupId"
                          element={<Lazy C={OrgGroupDetailPage} />}
                        />
                        <Route
                          path="org-groups/:groupId/settings"
                          element={<Lazy C={OrgGroupSettingsPage} />}
                        />
                        <Route
                          path="org-groups/:groupId/audit"
                          element={<Lazy C={OrgGroupAuditPage} />}
                        />
                        {/* Sprint 11.6.0 Shared Data routes */}
                        <Route
                          path="org-groups/:groupId/shared/vendors"
                          element={<Lazy C={SharedVendorsPage} />}
                        />
                        <Route
                          path="org-groups/:groupId/shared/frameworks"
                          element={<Lazy C={SharedFrameworksPage} />}
                        />
                        <Route
                          path="org-groups/:groupId/shared/form-templates"
                          element={<Lazy C={SharedFormTemplatesPage} />}
                        />
                        <Route
                          path="org-groups/:groupId/settings/inheritance"
                          element={<Lazy C={SettingsInheritancePage} />}
                        />
                        <Route
                          path="org-groups/:groupId/merged/vendors"
                          element={<Lazy C={MergedVendorsPage} />}
                        />
                        {/* Sprint 11.7.0 Cross-Org routes */}
                        <Route
                          path="org-groups/:groupId/users"
                          element={<Lazy C={GroupUsersPage} />}
                        />
                        <Route
                          path="org-groups/:groupId/audit/consolidated"
                          element={<Lazy C={GroupAuditPage} />}
                        />
                        {/* Phase 9 Extension: Organization Management */}
                        <Route path="organizations" element={<Lazy C={OrganizationsPage} />} />
                        <Route
                          path="organizations/create"
                          element={<Lazy C={OrganizationFormPage} />}
                        />
                        <Route
                          path="organizations/:orgId"
                          element={<Lazy C={OrganizationFormPage} />}
                        />
                        {/* Phase 9 Extension: Public Intake Configs */}
                        <Route path="intake-configs" element={<Lazy C={IntakeConfigsPage} />} />
                        <Route
                          path="intake-configs/create"
                          element={<Lazy C={IntakeConfigFormPage} />}
                        />
                        <Route
                          path="intake-configs/:configId"
                          element={<Lazy C={IntakeConfigFormPage} />}
                        />
                        {/* Sprint 22.3.0 Organizations Maturity routes */}
                        {/* Sprint 43.2.0 batch 3 (E2) — DepartmentsAdminPage supersedes DepartmentsPage at /admin/organizations/departments. */}
                        <Route
                          path="organizations/departments"
                          element={<Lazy C={DepartmentsAdminPage} />}
                        />
                        <Route
                          path="organizations/departments-legacy"
                          element={<Lazy C={DepartmentsPage} />}
                        />
                        {/* Sprint 43.2.0 batch 3 (E4) — Retention Policies admin */}
                        <Route
                          path="organizations/retention-policies"
                          element={<Lazy C={RetentionPoliciesAdminPage} />}
                        />
                        <Route
                          path="organizations/entity-frameworks"
                          element={<Lazy C={EntityFrameworkAssignmentsPage} />}
                        />
                        <Route
                          path="organizations/compliance-rollup"
                          element={<Lazy C={ComplianceRollupPage} />}
                        />
                        {/* Sprint 4.1.0 Privacy Scanner routes */}
                        <Route
                          path="privacy-scanner/monitoring"
                          element={<Lazy C={MonitoringDashboardPage} />}
                        />
                        <Route
                          path="privacy-scanner/monitoring/settings"
                          element={<Lazy C={MonitoringSettingsPage} />}
                        />
                        <Route
                          path="privacy-scanner/real-time"
                          element={<Lazy C={RealTimeScanPage} />}
                        />
                        <Route
                          path="privacy-scanner/real-time/settings"
                          element={<Lazy C={ScannerSettingsPage} />}
                        />
                        <Route
                          path="privacy-scanner/real-time/results"
                          element={<Lazy C={ScanResultsArchivePage} />}
                        />
                        <Route
                          path="privacy-scanner/real-time/results/:scanId"
                          element={<Lazy C={ScanResultDetailPage} />}
                        />
                        {/* Sprint 28.7.1 Consolidation routes (UC-VN-11) */}
                        <Route path="consolidation" element={<Lazy C={ConsolidationCasesPage} />} />
                        <Route
                          path="consolidation/create"
                          element={<Lazy C={ConsolidationCaseCreatePage} />}
                        />
                        <Route
                          path="consolidation/:caseId"
                          element={<Lazy C={ConsolidationCaseDetailPage} />}
                        />
                        <Route
                          path="consolidation/sections/:sectionId"
                          element={<Lazy C={SectionInputPage} />}
                        />
                        <Route
                          path="consolidation/:caseId/review"
                          element={<Lazy C={ConsolidationReviewPage} />}
                        />
                        <Route
                          path="consolidation/:caseId/export"
                          element={<Lazy C={DossierExportPage} />}
                        />
                      </Route>
                      {/* end PermissionGate */}
                    </Route>

                    {/* Default redirect */}
                    <Route path="/" element={<Navigate to="/rights-request" replace />} />
                  </Routes>
                </SiteSetupGuard>
              </ModuleConfigProvider>
            </PermissionProvider>
          </AuthProvider>
        </ToastProvider>
      </ErrorBoundary>
    </LocaleProvider>
  );
}
